import React, { useCallback, useState, useEffect, useId, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch, detailMessage } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import InfoTip from '../components/InfoTip';
import {
  Badge, CardSkeleton, CopyButton, EmptyState, Field, Icon, PageHeader, Switch, Tabs, cx,
} from '../components/ui';
import usePhoneCalls, { usePhoneStatus } from '../utils/usePhoneCalls';

/* The page's three tabs. External is everything proxy-tools does for an agent that is not
   one of the other two; MCP is the gateways an agent reaches other services through
   (RossGate, and the MCP servers such as Syrto); Voice is what the agent hears and speaks
   with, and where phone calls are set. The open tab is `?tab=` in the URL. */
const TABS = [
  { value: 'external', label: 'External', icon: 'globe' },
  { value: 'mcp', label: 'MCP', icon: 'server' },
  { value: 'voice', label: 'Voice', icon: 'voice' },
];

// "19 Jul 2026": a month name cannot be misread as D/M vs M/D.
const formatDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : null);

// "Qwen 3.6 (Custom vLLM)". A model created after the provider collapse has no provider,
// and the old "Qwen 3.6 ()" read as a rendering fault.
const modelLabel = (name, provider) => (provider ? `${name} (${provider})` : name);

/* Stored or not, for one config key. Unset is warn, not danger: several of these have
   a fallback, so an empty value is worth a look rather than an alarm. */
function SetBadge({ set }) {
  return set
    ? <Badge tone="ok" dot>Configured</Badge>
    : <Badge tone="warn" dot>Missing</Badge>;
}

/* Every section opens the same way, title on the left and its state on the right, so
   the page reads top to bottom as a checklist. `tip` is the section's InfoTip, written out
   as an element where the section is rather than passed as text, so the copy test measures
   it: the line under the title says what the section is, the tip holds what matters only
   when deciding something or when something is off. */
function SectionHead({ icon, title, status, tip }) {
  return (
    <div className="card-header">
      <h2 className="card-title">
        <Icon name={icon} className="text-muted" />
        {title}
        {tip}
      </h2>
      {status && <div className="card-actions">{status}</div>}
    </div>
  );
}

/* A setting with its switch on the right. Whatever the setting reveals goes in
   `children`, under the description, so it reads as part of the same row. */
function SettingRow({ id, title, description, checked, onChange, disabled, children }) {
  return (
    <div className="setting-row">
      <div className="grow min-w-0">
        <label className="setting-row-title" htmlFor={id}>{title}</label>
        {description && <div className="setting-row-desc">{description}</div>}
        {children}
      </div>
      <Switch id={id} checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

/* Collapsible read-only detail. A real <button aria-expanded>: the collapsible this
   replaces was a clickable <div>, which a keyboard could not open. */
function Disclosure({ label, count, children }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="mt-4">
      <button type="button" className="btn btn-ghost btn-sm" aria-expanded={open}
              aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} />
        {label}
        {count != null && <Badge tone="neutral">{count}</Badge>}
      </button>
      <div id={panelId} className="mt-3" hidden={!open}>{children}</div>
    </div>
  );
}

/* One stored config value: a read-only view of what is saved (masked, for a secret)
   and an editor that starts EMPTY. The saved value is never prefilled: for a secret the
   display is a mask, and saving it back would write the mask in as the key. Enter
   saves, Escape cancels. */
function ConfigField({ label, description, configKey, defaults, onSave, saving, placeholder,
                       secret, className }) {
  const inputId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const isSet = defaults?.[`${configKey}_set`];
  const display = defaults?.[`${configKey}_display`];
  const updatedAt = defaults?.[`${configKey}_updated_at`];

  const startEdit = () => { setDraft(''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(''); };
  const submitEdit = () => {
    if (draft.trim()) {
      onSave(configKey, draft.trim());
      setEditing(false);
      setDraft('');
    }
  };

  const hint = (description || updatedAt) ? (
    <>
      {description}
      {description && updatedAt ? ' ' : null}
      {updatedAt && <span className="text-faint">Updated {formatDate(updatedAt)}.</span>}
    </>
  ) : null;

  return (
    <Field label={label} htmlFor={inputId} hint={hint} className={className}
           extra={<SetBadge set={isSet} />}>
      <div className="input-group">
        {editing ? (
          <>
            <input
              id={inputId}
              type={secret ? 'password' : 'text'}
              className={cx('form-input', !secret && 'mono')}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={placeholder || 'Enter new value…'}
              autoFocus
              autoComplete={secret ? 'new-password' : 'off'}
              spellCheck={false}
              onKeyDown={(e) => { if (e.key === 'Enter') submitEdit(); if (e.key === 'Escape') cancelEdit(); }}
            />
            <button type="button" className="btn btn-primary" onClick={submitEdit}
                    disabled={saving || !draft.trim()}>
              Save
            </button>
            <button type="button" className="btn btn-ghost" onClick={cancelEdit}>Cancel</button>
          </>
        ) : (
          <>
            <input
              id={inputId}
              className={cx('form-input', isSet && 'mono')}
              value={isSet ? (display || '') : ''}
              placeholder="Not configured"
              readOnly
            />
            <button type="button" className="btn btn-secondary" onClick={startEdit} disabled={saving}
                    aria-label={`${isSet ? 'Change' : 'Set'} ${label}`}>
              {saving ? <span className="spinner-sm" /> : <Icon name="edit" />}
              {isSet ? 'Change' : 'Set'}
            </button>
          </>
        )}
      </div>
    </Field>
  );
}

function CaptioningConfig({ defaults, handleSave, saving, models, providers }) {
  const [useCustomKey, setUseCustomKey] = useState(false);
  const [useCustomEndpoint, setUseCustomEndpoint] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const selectId = useId();
  const keySwitchId = useId();
  const endpointSwitchId = useId();

  // Filter for multimodal models (input_types includes "image")
  const multimodalModels = (models || []).filter(m =>
    m.active && Array.isArray(m.input_types) && m.input_types.includes("image")
  );

  const currentModelKey = defaults?.vision_model_display || '';
  const selectedModel = models.find(m => m.model_key === currentModelKey);
  const selectedProvider = selectedModel ? providers.find(p => p.key === selectedModel.provider_key) : null;

  // Get provider's default endpoint/key from config_defaults
  const providerEndpointKey = selectedProvider?.config_key_endpoint;
  const providerApiKeyKey = selectedProvider?.config_key_api;
  const providerEndpointDefault = providerEndpointKey ? (defaults?.[`${providerEndpointKey}_display`] || defaults?.[providerEndpointKey]) : null;
  const providerApiKeyDefault = providerApiKeyKey ? (defaults?.[`${providerApiKeyKey}_display`] || '(configured)') : null;
  const providerName = selectedProvider?.ui_name || 'provider';

  // Check if custom values are set
  const hasCustomKey = defaults?.vision_api_key_set;
  const hasCustomEndpoint = defaults?.vision_endpoint_set;

  // Initialize custom state based on existing values
  useEffect(() => {
    setUseCustomKey(!!hasCustomKey);
    setUseCustomEndpoint(!!hasCustomEndpoint);
  }, [hasCustomKey, hasCustomEndpoint]);

  // A saved model the list cannot show would otherwise render as "Select a model",
  // the same controlled-<select> trap the Models page documents for its formats.
  const savedNotListed = !!currentModelKey && !multimodalModels.some(m => m.model_key === currentModelKey);

  // Switching back to the provider default DELETES the stored override, so it asks
  // first whenever there is one to lose. Switching to custom saves nothing by itself.
  const setKeySource = async (custom) => {
    if (custom) { setUseCustomKey(true); return; }
    if (hasCustomKey && !await confirm({
      title: 'Remove the custom API key?',
      body: <p>Tool calls go back to the {providerName} default. The stored key is deleted.</p>,
      confirmLabel: 'Remove key',
    })) return;
    setUseCustomKey(false);
    handleSave('vision_api_key', '');
  };

  const setEndpointSource = async (custom) => {
    if (custom) { setUseCustomEndpoint(true); return; }
    if (hasCustomEndpoint && !await confirm({
      title: 'Remove the custom endpoint?',
      body: <p>Tool calls go back to the {providerName} default. The stored URL is deleted.</p>,
      confirmLabel: 'Remove endpoint',
    })) return;
    setUseCustomEndpoint(false);
    handleSave('vision_endpoint', '');
  };

  const status = currentModelKey
    ? <Badge tone="ok" dot>Model set</Badge>
    : <Badge tone="warn" dot>No model</Badge>;

  return (
    <div className="card">
      <SectionHead
        icon="cpu"
        title="Proxy tools model"
        status={status}
        tip={(
          <InfoTip label="About the proxy tools model">
            Routed through proxy-llm on the provider's platform key, never on the user's own
            tier model. A custom key or endpoint replaces that provider's default for these
            calls only.
          </InfoTip>
        )}
      />
      {/* The DB key is still `vision_model`: see the note on the selector below. */}
      <p className="section-lede">
        The model behind every tool call that needs one, image captioning included.
      </p>

      {/* Model Selector. NB: the DB key here remains `vision_model` even
          though the section was renamed, keeping the key avoids a config
          migration for existing dev/stage/prod admin defaults. The field's
          purpose is now the generic proxy-tools model, not just vision
          captioning. */}
      <Field label="Model" htmlFor={selectId} hint="Only active models that accept images are listed.">
        <select
          id={selectId}
          className="form-input"
          value={currentModelKey}
          onChange={(e) => handleSave('vision_model', e.target.value)}
        >
          <option value="">Select a model</option>
          {savedNotListed && <option value={currentModelKey}>{currentModelKey} (not listed)</option>}
          {multimodalModels.map(m => (
            <option key={m.model_key} value={m.model_key}>
              {modelLabel(m.ui_name, m.provider_ui_name || m.provider_key)}
            </option>
          ))}
        </select>
      </Field>
      {multimodalModels.length === 0 && (
        <div className="notice notice-warn">
          <Icon name="alert" />
          <div className="notice-body">No multimodal models available. Add models with "image" in input_types.</div>
        </div>
      )}
      {savedNotListed && (
        <div className="notice notice-warn">
          <Icon name="alert" />
          <div className="notice-body">
            The saved model <code>{currentModelKey}</code> is not in this list: it is inactive,
            deleted, or does not accept images.
          </div>
        </div>
      )}

      {selectedModel && (
        <div className="mt-2">
          <SettingRow
            id={keySwitchId}
            title="Use a custom API key"
            description={useCustomKey
              ? `Stored below and used instead of the ${providerName} default.`
              : (providerApiKeyDefault
                ? <>Using {selectedProvider?.ui_name} API key: <code>{providerApiKeyDefault}</code></>
                : `Using the ${providerName} default.`)}
            checked={useCustomKey}
            onChange={setKeySource}
            disabled={!!saving.vision_api_key}
          >
            {useCustomKey && (
              <ConfigField
                label="Custom API key"
                configKey="vision_api_key"
                secret
                defaults={defaults} onSave={handleSave} saving={saving.vision_api_key}
                placeholder="Enter custom API key…"
                className="mt-3 mb-0"
              />
            )}
          </SettingRow>
          <SettingRow
            id={endpointSwitchId}
            title="Use a custom endpoint"
            description={useCustomEndpoint
              ? `Stored below and used instead of the ${providerName} default.`
              : (providerEndpointDefault
                ? <>Using {selectedProvider?.ui_name} endpoint: <code>{providerEndpointDefault}</code></>
                : `Using the ${providerName} default.`)}
            checked={useCustomEndpoint}
            onChange={setEndpointSource}
            disabled={!!saving.vision_endpoint}
          >
            {useCustomEndpoint && (
              <ConfigField
                label="Custom endpoint"
                configKey="vision_endpoint"
                defaults={defaults} onSave={handleSave} saving={saving.vision_endpoint}
                placeholder="https://…"
                className="mt-3 mb-0"
              />
            )}
          </SettingRow>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

/* Outbound message correction — proofread every agent reply before it is delivered.
   Not a tool the agent can call. It is owned by proxy-tools and invoked by the agent
   runtime BEFORE the reply is appended to the agent's history. It lives on this page
   because it is a platform-wide model choice, which is what the rest of this page is about.

   (⚠ This block used to open by calling it "post-processing proxy-channels applies on the
   way out" while its own closing paragraph said it was not proxy-channels — the opening
   sentence was never corrected when the correction was moved. Correcting on delivery WAS
   built, and abandoned: a correction applied after the reply is stored leaves the agent's
   record disagreeing with what the user actually read.)

   Three controls: the toggle, the model, and the prompt. The prompt was read-only until
   2026-09-23 and is now editable — see `CorrectionPromptEditor` below for what an admin can
   and cannot break with it, and the ⚠ on `OUTBOUND_CORRECTION_PROMPT` in
   `backend/services/config.py` for why the original read-only argument no longer holds. */
/* The correction's system prompt. Editable since 2026-09-23; it was a read-only <pre>.

   ⚠ THE ONLY DRAFT-HOLDING CONTROL ON THIS PAGE, and that is forced rather than chosen.
   Every other control here saves on change — a toggle, a dropdown, a one-line ConfigField
   that commits on Enter — which is why `OutboundCorrectionConfig` deliberately holds no
   state. A 2 KB prompt cannot work that way: saving per keystroke would write a row and
   refetch the page on every character. So this component owns a draft, and the price is
   that it has to resolve the one problem the others do not have — a refetch arriving while
   the operator is typing. `baseline` is the effective prompt as last loaded, and the draft
   is only re-seeded from props when the SAVED value actually changes, never merely because
   the parent re-rendered. */
function CorrectionPromptEditor({ defaults, handleSave, saving }) {
  const textareaId = useId();
  const [confirm, confirmDialog] = useConfirm();
  const effective = defaults?.outbound_correction_prompt_display || '';
  const platformDefault = defaults?.outbound_correction_prompt_default || '';
  const isCustom = !!defaults?.outbound_correction_prompt_set;

  const [draft, setDraft] = useState(effective);
  const [baseline, setBaseline] = useState(effective);

  // Re-seed ONLY when the saved value changed underneath us (our own save landing, or
  // another admin's). Keying the effect on `effective` rather than on `defaults` is what
  // stops an unrelated refetch — the page reloads every default after any save on this
  // page — from wiping a half-typed prompt.
  useEffect(() => {
    if (effective !== baseline) {
      setBaseline(effective);
      setDraft(effective);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective]);

  const dirty = draft !== baseline;
  const matchesDefault = draft.trim() === platformDefault.trim();

  // Empty DELETES the row (see `update_config_default`), which is exactly "use the platform
  // default" — so resetting is a save of '' rather than a save of the default text. Writing
  // the text back would store a row that merely happens to equal ours today and would then
  // stop tracking any future change to it.
  //
  // It asks first when there is a stored prompt to lose: the custom text is not kept
  // anywhere once the row is deleted.
  const reset = async () => {
    if (isCustom && !await confirm({
      title: 'Reset the correction prompt?',
      body: <p>The custom prompt is deleted and the platform default is used again.</p>,
      confirmLabel: 'Reset prompt',
    })) return;
    handleSave('outbound_correction_prompt', '');
  };
  const save = () => {
    const next = draft.trim();
    if (!next) { reset(); return; }
    handleSave('outbound_correction_prompt', next);
  };

  return (
    <div className="form-group mb-0">
      <div className="row-wrap gap-2 mb-2">
        <label className="form-label mb-0" htmlFor={textareaId}>System prompt</label>
        <InfoTip label="About the correction prompt">
          Used for the rewrite only. Language detection keeps its own fixed prompt, and the
          guards that discard a bad rewrite are enforced in code, so this text changes
          neither. Live within about 15 seconds of saving.
        </InfoTip>
        <Badge tone={isCustom ? 'info' : 'neutral'}>{isCustom ? 'Customised' : 'Platform default'}</Badge>
        {dirty && <Badge tone="warn" dot className="ml-auto">Unsaved changes</Badge>}
      </div>

      <textarea
        id={textareaId}
        className="form-input mono"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        rows={14}
      />

      <div className="row-wrap gap-2 mt-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={saving || !dirty}
        >
          {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save prompt'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setDraft(baseline)}
          disabled={saving || !dirty}
        >
          Discard
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={reset}
          /* Offered whenever a row exists, even if the draft already equals the default:
             the row is what makes the prompt stop tracking ours, so deleting it is a
             distinct act from typing the same words. */
          disabled={saving || !isCustom}
        >
          Reset to default
        </button>
        {isCustom && matchesDefault && !dirty && (
          <span className="text-xs text-muted">
            Identical to the default, but stored, so it will not track future changes to it.
          </span>
        )}
      </div>
      {confirmDialog}
    </div>
  );
}

function OutboundCorrectionConfig({ defaults, handleSave, saving, models, providers }) {
  const switchId = useId();
  const selectId = useId();
  // Any active model will do: rewriting is text in, text out. Contrast with the
  // captioning section above, which must filter on input_types including "image".
  const textModels = (models || []).filter(m => m.active);

  // Driven straight off the server payload rather than local state, matching the model
  // dropdown below it. There is no draft to hold: each control saves on change and the
  // page refetches, so component state could only be a way to show something stale.
  const enabled = String(defaults?.outbound_correction_enabled_display || '')
    .trim().toLowerCase() === 'true';
  const currentModelKey = defaults?.outbound_correction_model_display || '';
  const selectedModel = models.find(m => m.model_key === currentModelKey);
  const selectedProvider = selectedModel
    ? providers.find(p => p.key === selectedModel.provider_key)
    : null;
  // The prompt itself is `CorrectionPromptEditor`'s business — it holds a draft, which this
  // component deliberately does not. See the note on that component.

  const savedNotListed = !!currentModelKey && !textModels.some(m => m.model_key === currentModelKey);

  const status = !enabled
    ? <Badge tone="neutral">Off</Badge>
    : currentModelKey
      ? <Badge tone="ok" dot>On</Badge>
      : <Badge tone="warn" dot>On, no model</Badge>;

  return (
    <div className="card">
      <SectionHead
        icon="send"
        title="Outbound message correction"
        status={status}
        tip={(
          <InfoTip label="About outbound message correction" width={360}>
            <p>
              <strong>Italian is rewritten every time</strong>, even when already correct;
              other languages pass through untouched. A rewrite is discarded if it changes a
              number or a link, or drops much of the message.
            </p>
            <p>
              One extra model call per message, billed to that user. It never blocks a
              message: on any failure the agent's own text is delivered.
            </p>
          </InfoTip>
        )}
      />
      {/* Trimmed 2026-09-23. What was cut, in case it is wanted back: non-Italian replies
          answer the ALREADY_CORRECT sentinel rather than being skipped, the discard guards
          are enforced in code (`_rewrite_is_usable` in proxy/tools/tools_text.py) and not
          by the prompt, and the feature covers every channel because it runs in the runtime
          before the reply is appended to history. None of that changes what an operator
          does on this page. */}
      <p className="section-lede">Proofreads every agent reply before it is delivered.</p>

      <SettingRow
        id={switchId}
        title="Correct outbound messages"
        description="Live within about 15 seconds, no deploy."
        checked={enabled}
        disabled={!!saving.outbound_correction_enabled}
        /* "false", never "": an empty value DELETES the key, which reads as off but
           also discards the timestamp and makes "never configured" and "switched
           off" indistinguishable in the console. */
        onChange={(on) => handleSave('outbound_correction_enabled', on ? 'true' : 'false')}
      />

      {enabled && (
        <div className="mt-4">
          <Field label="Model" htmlFor={selectId}>
            <select
              id={selectId}
              className="form-input"
              value={currentModelKey}
              onChange={(e) => handleSave('outbound_correction_model', e.target.value)}
            >
              <option value="">Select a model</option>
              {savedNotListed && <option value={currentModelKey}>{currentModelKey} (not listed)</option>}
              {textModels.map(m => (
                <option key={m.model_key} value={m.model_key}>
                  {modelLabel(m.ui_name, providers.find(p => p.key === m.provider_key)?.ui_name
                                         || m.provider_key)}
                </option>
              ))}
            </select>
          </Field>
          {/* The toggle alone does nothing (the correction path needs a model as well
              before it will attempt anything), so say so rather than leaving it looking
              armed. */}
          {!currentModelKey && (
            <div className="notice notice-warn">
              <Icon name="alert" />
              <div className="notice-body">No model selected, so nothing is being corrected.</div>
            </div>
          )}
          {savedNotListed && (
            <div className="notice notice-warn">
              <Icon name="alert" />
              <div className="notice-body">
                The saved model <code>{currentModelKey}</code> is not in this list: it is
                inactive or deleted.
              </div>
            </div>
          )}
          {selectedModel && selectedProvider && (
            <div className="notice notice-ok">
              <Icon name="checkCircle" />
              <div className="notice-body">
                Using the {selectedProvider.ui_name} platform key. Not the user's model
                or quota.
              </div>
            </div>
          )}

          <CorrectionPromptEditor
            defaults={defaults}
            handleSave={handleSave}
            saving={!!saving.outbound_correction_prompt}
          />
        </div>
      )}
    </div>
  );
}

/* Speech to text: which Voice page model the agent's `stt` tool uses. The models themselves
   (endpoint, key) live on the Voice page, the way LLM models live on the Models page and the
   proxy tools model is picked here. Unset, `stt` falls back to the legacy whisper_endpoint /
   whisper_api_key keys. */
function SpeechToTextConfig({ defaults, handleSave, saving, voiceModels }) {
  const sttId = useId();
  const current = defaults?.stt_model_display || '';
  // A realtime row listens to a phone call as it happens; the stt tool sends it a file.
  const options = (voiceModels || []).filter((m) => m.kind === 's2t' && m.active && !m.streaming);
  const notListed = !!current && !options.some((m) => m.voice_key === current);
  return (
    <div className="card">
      <SectionHead
        icon="voice"
        title="Speech to text"
        tip={(
          <InfoTip label="About speech to text">
            Transcribes in the language the agent names. Realtime models serve phone calls
            only, so they are not listed here. With no model picked, the tool keeps the legacy
            Whisper endpoint and key.
          </InfoTip>
        )}
      />
      <p className="section-lede">
        Transcribes voice messages and audio files for the agent's <code>stt</code> tool.
        Models are added and tested on the <Link to="/voice">Voice</Link> page.
      </p>
      {/* One field: half the card's width, like the other single fields on this page. */}
      <div className="form-row">
        <Field label="Model" htmlFor={sttId} className="mb-0"
               hint="Only active speech-to-text models are listed.">
          <select id={sttId} className="form-input" value={current} disabled={!!saving.stt_model}
                  onChange={(e) => handleSave('stt_model', e.target.value)}>
            <option value="">Legacy Whisper settings</option>
            {notListed && <option value={current}>{current} (not listed)</option>}
            {options.map((m) => <option key={m.voice_key} value={m.voice_key}>{m.ui_name} ({m.provider_label})</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

const ttsRows = (entries) => (entries || []).map(({ language, model, voice }) => ({ language, model, voice }));

/* Text to speech: the languages the agent's `tts` tool speaks, and for each the Voice page
   model and the voice of that language that speak it (GET and PUT /api/tts-languages,
   backend api/routes/voice_models.py). The language of a call picks its row; another
   language is refused with the list, and the agent's tool describes `language` with that same
   list, shown under the table word for word. The first row is what a call that names no
   language gets. A draft, like RossGateDefaults: the table is saved as a whole, and a save
   that fails leaves every edit on screen. */
function TextToSpeechConfig() {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const idBase = useId();
  const [view, setView] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = async () => {
    try {
      const resp = await apiFetch('/api/tts-languages');
      if (!resp.ok) {
        setLoadFailed(true);
        return;
      }
      const body = await resp.json();
      setView(body);
      setRows(ttsRows(body.languages));
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
    }
  };

  useEffect(() => { load(); }, []);

  if (!view) {
    return (
      <div className="card">
        <SectionHead icon="broadcast" title="Text to speech" />
        {loadFailed ? (
          <div className="notice notice-danger mb-0" role="alert">
            <Icon name="alertCircle" />
            <div className="notice-body row-wrap between gap-3">
              <span>Could not load the text-to-speech languages.</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
                <Icon name="refresh" />
                Retry
              </button>
            </div>
          </div>
        ) : <CardSkeleton lines={4} />}
      </div>
    );
  }

  const catalog = view.catalog || { languages: [], models: [], voices: {} };
  const modelByKey = Object.fromEntries((catalog.models || []).map((m) => [m.voice_key, m]));
  const languageName = Object.fromEntries((catalog.languages || []).map((l) => [l.value, l.label]));
  const savedRows = ttsRows(view.languages);
  const savedByLanguage = Object.fromEntries((view.languages || []).map((e) => [e.language, e]));
  const dirty = JSON.stringify(rows) !== JSON.stringify(savedRows);
  const incomplete = rows.some((r) => !r.language || !r.model || !r.voice);
  const locked = !isSuper || saving;
  const used = new Set(rows.map((r) => r.language));
  const spoken = (view.languages || []).filter((e) => !e.problem).length;

  // The models that speak a language; a switched-off one stays listed, disabled, so a row
  // that names it still shows it.
  const modelsFor = (language) => (catalog.models || []).filter(
    (m) => m.speaks_any || (m.languages || []).includes(language));
  // A voice that speaks every language (the speech API's, ElevenLabs') has no language of its
  // own. An ElevenLabs model's voices are its account's, listed with its key.
  const voicesFor = (model, language) => (model?.account_voices
    || (catalog.voices || {})[model?.provider] || []).filter(
    (v) => v.language == null || v.language === language);
  const firstVoice = (model, language) => (model?.defaults || {})[language]
    || voicesFor(model, language)[0]?.value || '';
  const label = (r, i) => (r.language ? languageName[r.language] || r.language : `language ${i + 1}`);

  const setRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const changeLanguage = (i, language) => {
    const row = rows[i];
    const kept = modelByKey[row.model];
    const speaksIt = kept && kept.active && (kept.speaks_any || (kept.languages || []).includes(language));
    const model = speaksIt ? kept : modelsFor(language).find((m) => m.active);
    // A voice speaks one language, except the speech API's and ElevenLabs', which speak any.
    const voice = speaksIt && kept.speaks_any && row.voice ? row.voice : firstVoice(model, language);
    setRow(i, { language, model: model?.voice_key || '', voice });
  };
  const changeModel = (i, key) => {
    const row = rows[i];
    const model = modelByKey[key];
    const keep = model && row.voice && (model.speaks_any
      ? modelByKey[row.model]?.provider === model.provider
      : voicesFor(model, row.language).some((v) => v.value === row.voice));
    setRow(i, { model: key, voice: keep ? row.voice : firstVoice(model, row.language) });
  };
  const addRow = () => {
    // The first language not in the table that a model speaks, Italian and English first.
    const order = ['it', 'en', ...(catalog.languages || []).map((l) => l.value)];
    const language = order.find((c) => !used.has(c) && modelsFor(c).some((m) => m.active)) || '';
    const model = language ? modelsFor(language).find((m) => m.active) : null;
    setRows((prev) => [...prev, { language, model: model?.voice_key || '', voice: firstVoice(model, language) }]);
  };
  const removeRow = (i) => setRows((prev) => prev.filter((_, j) => j !== i));
  const moveRow = (i, by) => setRows((prev) => {
    const next = [...prev];
    const [row] = next.splice(i, 1);
    next.splice(i + by, 0, row);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const resp = await apiFetch('/api/tts-languages', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ languages: rows }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        // The table stays on screen, so nothing the admin picked is lost.
        toast.error(detailMessage(body.detail, 'Could not save the languages'));
        return;
      }
      setView(body);
      setRows(ttsRows(body.languages));
      toast.success('Text-to-speech languages saved');
    } catch (e) {
      toast.error('Could not save the languages');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <SectionHead
        icon="broadcast"
        title="Text to speech"
        status={spoken
          ? <Badge tone="info" dot>{spoken} {spoken === 1 ? 'language' : 'languages'}</Badge>
          : <Badge tone="warn" dot>Speaks nothing</Badge>}
        tip={(
          <InfoTip label="About text to speech">
            The agent names the language of every voice reply. A language not in this table is
            refused, and the agent answers in text instead. Live within about 15 seconds of
            saving.
          </InfoTip>
        )}
      />
      <p className="section-lede">
        The languages the agent's <code>tts</code> tool speaks, each with its model and voice.
        Models are added and tested on the <Link to="/voice">Voice</Link> page.
      </p>

      {!isSuper && (
        <div className="notice notice-info" role="status">
          <Icon name="lock" />
          <div className="notice-body">Read only: a superadmin can change these.</div>
        </div>
      )}
      {!view.stored && (
        <div className="notice notice-info" role="status">
          <Icon name="info" />
          <div className="notice-body">
            Never saved: until this table is saved, the tool speaks Italian and English with
            Edge TTS, as below.
          </div>
        </div>
      )}

      {(catalog.models || []).length === 0 ? (
        <EmptyState icon="voice" title="No text-to-speech models">
          Add one on the <Link to="/voice">Voice</Link> page first.
        </EmptyState>
      ) : (
        <div className="table-container bordered">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Language</th>
                <th>Model</th>
                <th>Voice</th>
                <th><span className="sr-only">Order and remove</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted">
                    No language: the tool speaks none, so the agent replies in text.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => {
                const model = modelByKey[r.model];
                const models = modelsFor(r.language);
                const voices = voicesFor(model, r.language);
                const listedVoice = model?.account_voices
                  ? voices.find((v) => v.value === r.voice) : null;
                const languages = (catalog.languages || []).filter(
                  (l) => l.value === r.language || !used.has(l.value));
                const saved = savedByLanguage[r.language];
                // A saved row's problem, shown while the row is as saved.
                const problem = saved && saved.model === r.model && saved.voice === r.voice
                  ? saved.problem : null;
                const name = label(r, i);
                return (
                  // eslint-disable-next-line react/no-array-index-key -- rows have no identity until saved
                  <tr key={i}>
                    <td>
                      <select id={`${idBase}-language-${i}`} className="form-input" value={r.language}
                              disabled={locked} aria-label={`Language ${i + 1}`}
                              onChange={(e) => changeLanguage(i, e.target.value)}>
                        {!r.language && <option value="">Pick a language</option>}
                        {languages.map((l) => <option key={l.value} value={l.value}>{l.label} ({l.value})</option>)}
                      </select>
                      {i === 0 && <span className="cell-sub">Also when the agent names no language</span>}
                    </td>
                    <td>
                      <select className="form-input" value={r.model} disabled={locked || !r.language}
                              aria-label={`Model for ${name}`}
                              onChange={(e) => changeModel(i, e.target.value)}>
                        {!model && (
                          <option value={r.model}>
                            {r.model ? `${r.model} (not on the Voice page)` : 'Pick a model'}
                          </option>
                        )}
                        {models.map((m) => (
                          <option key={m.voice_key} value={m.voice_key} disabled={!m.active}>
                            {m.ui_name} ({m.provider_label}){m.active ? '' : ', switched off'}
                          </option>
                        ))}
                      </select>
                      {r.language && models.length === 0 && (
                        <span className="cell-sub text-warn">No model on the Voice page speaks this language.</span>
                      )}
                    </td>
                    <td>
                      {model?.speaks_any ? (
                        <>
                          <input className="form-input mono" value={r.voice} disabled={locked}
                                 list={`${idBase}-voices-${i}`} spellCheck={false}
                                 placeholder={model.provider === 'elevenlabs' ? 'Voice ID' : 'alloy'}
                                 aria-label={`Voice for ${name}`}
                                 onChange={(e) => setRow(i, { voice: e.target.value.trim() })} />
                          <datalist id={`${idBase}-voices-${i}`}>
                            {voices.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                          </datalist>
                          {/* An account's voice is an ID: its name, when the account lists it. */}
                          {listedVoice && <span className="cell-sub">{listedVoice.label}</span>}
                          {model.account_voices_error && (
                            <span className="cell-sub text-warn" title={model.account_voices_error}>
                              The account&apos;s voices could not be listed: type a voice ID.
                            </span>
                          )}
                        </>
                      ) : (
                        <select className="form-input" value={r.voice} disabled={locked || !model}
                                aria-label={`Voice for ${name}`}
                                onChange={(e) => setRow(i, { voice: e.target.value })}>
                          {!voices.some((v) => v.value === r.voice) && (
                            <option value={r.voice}>
                              {r.voice ? `${r.voice} (does not speak this language)` : 'Pick a voice'}
                            </option>
                          )}
                          {voices.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                      )}
                      {problem && <span className="cell-sub text-warn">{problem}</span>}
                    </td>
                    <td>
                      <div className="row gap-1 row-end">
                        <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                disabled={locked || i === 0} onClick={() => moveRow(i, -1)}
                                aria-label={`Move ${name} up`} title="Move up">
                          <Icon name="arrowUp" />
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                disabled={locked || i === rows.length - 1} onClick={() => moveRow(i, 1)}
                                aria-label={`Move ${name} down`} title="Move down">
                          <Icon name="arrowDown" />
                        </button>
                        <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                disabled={locked} onClick={() => removeRow(i)}
                                aria-label={`Remove ${name}`} title="Remove">
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="row-wrap between gap-3 mt-3">
        <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}
                disabled={locked || rows.length >= (view.max || 0) || (catalog.models || []).length === 0}>
          <Icon name="plus" /> Add a language
        </button>
        <div className="row-wrap gap-2">
          {dirty && <Badge tone="warn">Unsaved changes</Badge>}
          {dirty && incomplete && (
            <span className="text-sm text-muted">Pick a model and a voice for each language.</span>
          )}
          <button type="button" className="btn btn-ghost" disabled={!dirty || saving}
                  onClick={() => setRows(savedRows)}>
            Discard changes
          </button>
          <button type="button" className="btn btn-primary"
                  disabled={(!dirty && view.stored) || incomplete || locked} onClick={save}>
            {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save'}
          </button>
        </div>
      </div>

      <Disclosure label="What the tool tells the agent">
        <p className="text-sm text-muted mb-3">
          How the agent's tool describes <code>language</code>, for the saved table.
        </p>
        <div className="card-inset">{view.agent_description}</div>
      </Disclosure>
    </div>
  );
}

/* Phone calls: what the telephony service needs from the platform, and everything a call's
   experience depends on. Rendered only where the environment takes calls (`usePhoneCalls`).

   The two addresses come from terraform's voice_public_base_url (admin-console's
   VOICE_WEBHOOK_URL and VOICE_STREAM_URL, answered by /api/phone/status). The service is
   given the voice URL only: voice-bridge answers each call with the audio stream's address
   and a pass for that one call, and refuses a stream opened without it. The stream is shown
   so it can be recognised in the service's call logs and the bridge's.

   The auth token's config key is still `twilio_auth_token` (renaming it would orphan the
   token already saved); nothing the operator reads names a vendor. It is a superadmin's to
   set like every key on this page, and the only place it is set: there is no secret or
   variable behind it. voice-bridge reads it through proxy-channels within a minute (at once
   when a call's signature does not match); while it is missing, every call is refused.

   The account id beside it (`twilio_account_sid`) is read the same way and is needed only to
   PLACE a call — the `place_call` tool, granted per user on the Tools page. Calls still
   ARRIVE without it, which is why it is a field of its own rather than part of the token's:
   an environment that only answers calls leaves it empty. Shown in full, because it is on
   every request the service sends and opens nothing on its own; the pair being printed one
   under the other on the vendor's console is why each field says which is which, and why
   both are checked on save. There is deliberately no outbound NUMBER here: a call goes out
   on the owner's own line, from their Phone row. */
const PHONE_SETTINGS = [
  {
    to: '/tiers',
    link: 'Open Tiers',
    title: 'Call models and voice',
    description: 'Per tier, under Phone calls: what a call listens, speaks and thinks with.',
  },
  {
    to: '/users',
    link: 'Open Users',
    title: 'Phone line',
    description: 'Per user, under Edit, Account: the number they call and the one allowed to call it.',
  },
  {
    to: '/voice',
    link: 'Open Voice',
    title: 'Realtime speech to text',
    description: 'The models a call listens with.',
  },
];

/* Everything a call's experience depends on, in tabs: the addresses the telephony service
   needs, the numbers of a turn, how the agent behaves, and the words it runs on.

   NOTHING here is a list the frontend maintains. The settings and the prompts come from the
   backend's config defaults (`call_timings` and `call_prompts`, from shared/call_timings.py
   and shared/call_prompts.py, which are the same definitions voice-bridge runs calls with),
   so a setting added there appears here with its own default, range, unit, group, direction
   and explanation, and the console can never state a default the calls do not use.

   ⚠ THERE IS NO HINTS MAP HERE ANY MORE (28 Oct 2026). The page used to carry one sentence
   per setting, written beside the field — a second description of each setting, in the file
   nobody edits when they change the behaviour, and it had already drifted. The detail is
   `spec.help` from shared/call_timings.py now, and `spec.direction_label` says which kind of
   call the setting reaches, so a setting added there arrives here fully explained.

   The audio format is deliberately absent and not settable: the sample rates, the 20 ms
   frame, the filter and its cut-off are what the phone line and the models require. A wrong
   value there does not change the experience, it ends the call. */

const UNIT_WORD = { s: 'seconds', ms: 'milliseconds', chars: 'characters', count: '', p: '' };

// "1 second", not "1 seconds". Several defaults are exactly 1.
const unitFor = (unit, value) => {
  const word = UNIT_WORD[unit] ?? '';
  return word && Number(value) === 1 ? word.replace(/s$/, '') : word;
};

/* Inbound / Outbound / Both, beside the Default badge: ten of the seventeen prompts and
   fifteen of the twenty-nine settings do nothing at all to one of the two directions, and
   before this the only way to know which was to read the bridge (Mark, 2026-10-28). The
   words come from the backend (shared/call_directions.py) so the two tabs cannot end up
   calling the same thing by two different names. `outline` rather than a colour: it is a
   label on a different axis from Default/Customised, not a second status. */
function DirectionBadge({ spec }) {
  if (!spec.direction_label) return null;
  return <Badge tone="outline">{spec.direction_label}</Badge>;
}

/* One setting: its name and the `i` on the left, the value and its buttons on the right.
   The value is editable in place; unlike ConfigField the field starts from the stored value,
   because a number is not a secret and so there is no mask to save back by mistake. "Use the
   default" saves an empty value, which deletes the row. Enter saves, Escape puts back the
   value in use.

   The `i` carries `spec.help`, which is paragraphs of explanation ending in an example and
   is deliberately longer than the 400-character cap
   tests/test_frontend_copy_standard.py puts on an InfoTip written in JSX. That cap is about
   prose a page author types into a panel; this is the setting's own documentation, asked for
   in those words (Mark, 2026-10-28), and it lives in shared/call_timings.py where the
   behaviour is defined. The cap's regex strips `{…}` and so does not measure it — noted here
   rather than left to look like an accident.

   ⚠ A SETTINGS ROW, NOT A FORM FIELD IN A GRID (28 Oct 2026). These used to sit two to a
   row in `form-row`, each as a stacked Field: a 0.05-step number beside a 4-digit character
   count, so no two inputs were the same width, no two Save buttons lined up, and reading
   down one column meant tracking across the other. Now the controls live in a fixed three-
   column track (.timing-control), so every input, every Save and every reset sits on the
   same vertical line whatever the row.

   The reset button is ALWAYS rendered, disabled when the setting is already the default.
   Rendering it only when customised is what shifted the Save buttons: a customised row was
   one button wider than the row above it. */
function TimingField({ spec, defaults, onSave, saving }) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const isSet = !!defaults?.[`${spec.key}_set`];
  const inUse = isSet ? String(defaults?.[`${spec.key}_display`] ?? '') : String(spec.default);
  const [draft, setDraft] = useState(inUse);
  useEffect(() => { setDraft(inUse); }, [inUse]);

  const whole = spec.unit === 'chars' || spec.unit === 'count' || spec.unit === 'ms';
  const text = draft.trim().replace(',', '.');
  const value = Number(text);
  const unit = UNIT_WORD[spec.unit] ?? '';
  const defaultUnit = unitFor(spec.unit, spec.default);
  const inRange = Number.isFinite(value) && value >= spec.low && value <= spec.high;
  const valid = text !== '' && inRange && (!whole || Number.isInteger(value));
  const changed = text !== inUse;
  const save = () => { if (valid && changed && !saving) onSave(spec.key, text); };
  const error = text === '' || valid ? null
    : !inRange ? `From ${spec.low} to ${spec.high}${unit ? ` ${unit}` : ''}.`
      : `A whole number${unit ? ` of ${unit}` : ''}.`;

  return (
    <div className="setting-row timing-row">
      <div className="grow min-w-0">
        {/* The label does NOT wrap the badges or the `i`: a click anywhere inside a
            `<label for>` is forwarded to the control, so an `i` nested in it would select
            the number field every time the tip was opened. */}
        <div className="setting-row-title row-wrap gap-2">
          <label className="mb-0" htmlFor={inputId}>{spec.name}</label>
          <Badge tone={isSet ? 'info' : 'neutral'}>{isSet ? 'Custom' : 'Default'}</Badge>
          <DirectionBadge spec={spec} />
          <InfoTip label={`About ${spec.name}`} width={420}>
            {(spec.help || []).map((para, i) => <p key={i}>{para}</p>)}
            <p>
              <strong>{spec.direction_label}.</strong>
              {' '}
              {spec.direction_about}
            </p>
            <p>
              Default {String(spec.default)}
              {defaultUnit ? ` ${defaultUnit}` : ''}, and anything from {spec.low} to{' '}
              {spec.high} may be saved. Read when a call starts, so a change is used from
              the next call and never interrupts one in progress.
            </p>
          </InfoTip>
        </div>
        <div className="setting-row-desc" id={hintId}>
          Default {spec.default}{defaultUnit ? ` ${defaultUnit}` : ''}, from {spec.low} to{' '}
          {spec.high}.
        </div>
        {error && <div className="form-error" id={errorId} role="alert">{error}</div>}
      </div>
      <div className="timing-control">
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          className="form-input num"
          min={spec.low}
          max={spec.high}
          step={spec.step}
          value={draft}
          aria-describedby={error ? `${errorId} ${hintId}` : hintId}
          aria-invalid={error ? true : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setDraft(inUse); }}
        />
        <button type="button" className="btn btn-primary" onClick={save}
                disabled={saving || !valid || !changed}>
          {saving ? <span className="spinner-sm" /> : 'Save'}
        </button>
        <button type="button" className="btn btn-secondary" disabled={saving || !isSet}
                onClick={() => onSave(spec.key, '')}>
          Use the default
        </button>
      </div>
    </div>
  );
}

/* One prompt or spoken line. Same shape as the correction prompt's editor: empty DELETES the
   row, which is what "use the default" means, so resetting saves '' rather than writing the
   default text back (a row that merely equals ours today would stop tracking it).

   The placeholders are shown because the backend REFUSES a text that drops one the call fills
   in, or invents one it does not know: such a text would raise on every call in the
   environment, not just the next one. `optional` ones (the placed call's Opening line) are
   filled in when used and may be left out, so they are listed apart from the ones to keep. */
function PromptEditor({ spec, defaults, onSave, saving }) {
  const textareaId = useId();
  const isSet = !!defaults?.[`${spec.key}_set`];
  const inUse = isSet ? String(defaults?.[`${spec.key}_display`] ?? '') : spec.default;
  const [draft, setDraft] = useState(inUse);
  const [baseline, setBaseline] = useState(inUse);

  // Re-seed only when the saved text changed underneath us, so an unrelated refetch (the
  // page reloads every default after any save) cannot wipe a half-typed prompt.
  useEffect(() => {
    if (inUse !== baseline) { setBaseline(inUse); setDraft(inUse); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inUse]);

  const dirty = draft !== baseline;
  const missing = (spec.needs || []).filter((n) => !draft.includes(`{${n}}`));
  const save = () => {
    const next = draft.trim();
    if (!next) { onSave(spec.key, ''); return; }
    onSave(spec.key, next);
  };

  return (
    <div className="form-group mb-3">
      <div className="row-wrap gap-2 mb-1">
        <label className="form-label mb-0" htmlFor={textareaId}>{spec.name}</label>
        <Badge tone={isSet ? 'info' : 'neutral'}>{isSet ? 'Customised' : 'Default'}</Badge>
        <DirectionBadge spec={spec} />
        <InfoTip label={`Which calls ${spec.name} is used on`} width={400}>
          <p>
            <strong>{spec.direction_label}.</strong>
            {' '}
            {spec.direction_about}
          </p>
          <p>
            Every prompt is sent to every call: the direction says which kind of call this
            one has an effect on, not which one receives it.
          </p>
        </InfoTip>
        {dirty && <Badge tone="warn" dot className="ml-auto">Unsaved</Badge>}
      </div>
      <div className="text-sm text-faint mb-2">{spec.about}</div>
      {(spec.needs || []).length > 0 && (
        <div className="text-xs text-muted mb-2">
          Must keep:{' '}
          {spec.needs.map((n) => (
            <code key={n} className={missing.includes(n) ? 'text-danger' : undefined}>{`{${n}}`}</code>
          )).reduce((all, el) => (all.length ? [...all, ' ', el] : [el]), [])}
          {missing.length > 0 && <span className="text-danger">{' '}— missing, the save will be refused.</span>}
        </div>
      )}
      {(spec.optional || []).length > 0 && (
        <div className="text-xs text-muted mb-2">
          May use:{' '}
          {spec.optional.map((n) => <code key={n}>{`{${n}}`}</code>)
            .reduce((all, el) => (all.length ? [...all, ' ', el] : [el]), [])}
        </div>
      )}
      <textarea
        id={textareaId}
        className="form-input mono"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        rows={spec.rows || 8}
      />
      <div className="row-wrap gap-2 mt-2">
        <button type="button" className="btn btn-primary btn-sm" onClick={save}
                disabled={saving || !dirty || missing.length > 0}>
          {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => setDraft(baseline)} disabled={saving || !dirty}>
          Discard
        </button>
        <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => onSave(spec.key, '')} disabled={saving || !isSet}>
          Use the default
        </button>
      </div>
    </div>
  );
}

/* A read-only address with its copy button, for pasting somewhere else. The field stays
   selectable by hand, for a browser that refuses the clipboard. */
function AddressField({ label, value, description }) {
  const inputId = useId();
  return (
    <Field label={label} htmlFor={inputId} hint={description}>
      <div className="input-group">
        <input id={inputId} className="form-input mono" value={value || ''} readOnly
               placeholder="Not known: voice_public_base_url is not set in terraform" />
        {value && <CopyButton value={value} label={`Copy the ${label.toLowerCase()}`} />}
      </div>
    </Field>
  );
}

const PHONE_TABS = [
  { value: 'endpoints', label: 'Endpoints', icon: 'globe' },
  { value: 'timings', label: 'Timings', icon: 'clock' },
  { value: 'behaviour', label: 'Behaviour', icon: 'sliders' },
  { value: 'prompts', label: 'System prompt', icon: 'edit' },
];

function PhoneCallsCard({ defaults, handleSave, saving }) {
  const phone = usePhoneStatus();
  const [tab, setTab] = useState('endpoints');
  const settings = defaults?.call_timings || [];
  const prompts = defaults?.call_prompts || [];

  // The groups in the order the definition gives them, each under its own tab.
  const groupsFor = (which) => {
    const out = [];
    settings.filter((s) => s.tab === which).forEach((s) => {
      const group = out.find((g) => g.key === s.group);
      if (group) group.items.push(s);
      else out.push({ key: s.group, label: s.group_label, items: [s] });
    });
    return out;
  };
  const roles = prompts.filter((p) => p.group === 'prompts');
  const lines = prompts.filter((p) => p.group === 'lines');

  /* The Timings and Behaviour tabs are the same thing twice: the groups the definition
     assigns to that tab, one setting per row. */
  const settingsPanel = (which) => (
    <div className="mt-3">
      <p className="text-sm text-faint mb-3">
        {which === 'timings'
          ? 'In the order a caller’s turn meets them. Shorter values answer sooner and cut a slow speaker’s sentence in two more often; a sentence cut in two is sent again whole when the rest arrives.'
          : 'How the agent behaves while it is working: what stops it talking, and how its requests queue.'}
        {' '}
        Each badge says which kind of call the setting reaches, and each
        {' '}<code>i</code> explains what the value does, with an example.
      </p>
      {groupsFor(which).map((group) => (
        <div key={group.key} className="card-inset mb-3">
          <span className="text-sm">{group.label}</span>
          {/* One column, in the order the definition gives them. `form-row` put two of
              these side by side and nothing lined up across the gap; see TimingField. */}
          <div className="mt-2">
            {group.items.map((spec) => (
              <TimingField key={spec.key} spec={spec} defaults={defaults}
                           onSave={handleSave} saving={!!saving[spec.key]} />
            ))}
          </div>
        </div>
      ))}
      {groupsFor(which).length === 0 && (
        <p className="text-sm text-faint">Nothing to set here on this environment.</p>
      )}
    </div>
  );

  return (
    <div className="card">
      <SectionHead
        icon="phone"
        title="Phone calls"
        status={<Badge tone="ok" dot>On</Badge>}
        tip={(
          <InfoTip label="About phone calls">
            The telephony service is given the voice URL only: the bridge answers each call
            with the audio stream and a pass for that call, so a stream set up by hand is
            refused. Everything on the other tabs is read when a call starts, so a change is
            used from the next call and never interrupts one in progress. Where
            <code>voice_channel_enabled</code> is off, every phone setting is hidden.
          </InfoTip>
        )}
      />
      <p className="section-lede">
        The addresses the telephony service needs, and everything the caller’s experience
        depends on.
      </p>
      <Tabs tabs={PHONE_TABS} value={tab} onChange={setTab} variant="line" size="sm"
            label="Phone call settings" idPrefix="phone" />

      {/* Each tab is a real tabpanel that stays MOUNTED, as the MCP server's tabs are.
          Two reasons it is not the shorter `tab === 'x' && …`: `Tabs` points every tab's
          `aria-controls` at `phone-panel-<value>`, so without these ids it referred to
          elements that did not exist; and a half-typed prompt was lost the moment you
          looked at another tab. */}
      <div role="tabpanel" id="phone-panel-endpoints" aria-labelledby="phone-tab-endpoints"
           hidden={tab !== 'endpoints'}>
        <div className="mt-3">
          <AddressField
            label="Voice URL"
            value={phone?.webhook_url}
            description="Set this on the number, as the webhook called when a call comes in (HTTP POST)."
          />
          <AddressField
            label="Audio stream"
            value={phone?.stream_url}
            description="Sent by the bridge on every call, with a pass for that call: nothing to paste."
          />
          <div className="form-row">
            <ConfigField
              label="Auth token"
              description="The account token the bridge checks every call's signature with. Without it every call is refused; a change is used within a minute."
              configKey="twilio_auth_token"
              secret
              defaults={defaults}
              onSave={handleSave}
              saving={saving.twilio_auth_token}
              placeholder="Enter the account auth token…"
            />
            <ConfigField
              label="Account id"
              description="Needed only to MAKE calls, for agents holding Place a Phone Call: calls still arrive without it. Shown in full — it is on every request the service sends — and it is the field above that is the secret."
              configKey="twilio_account_sid"
              defaults={defaults}
              onSave={handleSave}
              saving={saving.twilio_account_sid}
              placeholder="AC…"
            />
          </div>
          {PHONE_SETTINGS.map((s) => (
            <div key={s.to} className="setting-row">
              <div className="grow min-w-0">
                <div className="setting-row-title">{s.title}</div>
                <div className="setting-row-desc">{s.description}</div>
              </div>
              <Link to={s.to} className="btn btn-secondary btn-sm">
                {s.link}
                <Icon name="arrowRight" />
              </Link>
            </div>
          ))}
        </div>
      </div>

      <div role="tabpanel" id="phone-panel-timings" aria-labelledby="phone-tab-timings"
           hidden={tab !== 'timings'}>
        {settingsPanel('timings')}
      </div>

      <div role="tabpanel" id="phone-panel-behaviour" aria-labelledby="phone-tab-behaviour"
           hidden={tab !== 'behaviour'}>
        {settingsPanel('behaviour')}
      </div>

      <div role="tabpanel" id="phone-panel-prompts" aria-labelledby="phone-tab-prompts"
           hidden={tab !== 'prompts'}>
        <div className="mt-3">
          <p className="text-sm text-faint mb-3">
            What the models are told, and the words the caller hears. Each one starts as the
            text the calls run on today, so an untouched field is exactly what is in use. A
            call reads them when it starts.
          </p>
          <div className="card-inset mb-3">
            <span className="text-sm row gap-1">
              Instructions
              <InfoTip label="About the call prompts">
                Several of these were tuned against recorded calls, so a change is best
                tried on one call before it is left in place. A text that drops a
                placeholder the call fills in is refused when you save it, rather than
                failing on every call afterwards.
              </InfoTip>
            </span>
            <div className="mt-3">
              {roles.map((spec) => (
                <PromptEditor key={spec.key} spec={spec} defaults={defaults}
                              onSave={handleSave} saving={!!saving[spec.key]} />
              ))}
            </div>
          </div>
          <div className="card-inset mb-3">
            <span className="text-sm">Spoken lines</span>
            <div className="mt-3">
              {lines.map((spec) => (
                <PromptEditor key={spec.key} spec={spec} defaults={defaults}
                              onSave={handleSave} saving={!!saving[spec.key]} />
              ))}
            </div>
          </div>
          {prompts.length === 0 && (
            <p className="text-sm text-faint">Nothing to set here on this environment.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* Web Search — Brave Search is the only backend (SearXNG was removed). */

/* The Defaults for new users table the External tab, RossGate and the MCP servers share
   (Mark, 2026-11-02): per row, User managed (whether a new user's apps may see and change
   it, services/tool_user_managed.py) and On for new users. Both are read when a user is
   created, by every creation path, and never applied to a user that exists. A mandatory
   row is listed and fixed. `rows`: { id, name, sub, badge, mandatory, managed, granted,
   grantedLocked }; `managed` null means unknown and renders disabled. */
function DefaultsTable({ rows, locked, onManaged, onGranted, firstColumn = 'Tool' }) {
  const idBase = useId();
  return (
    <div className="table-container bordered">
      <table className="table table-compact">
        <thead>
          <tr>
            <th>{firstColumn}</th>
            <th>User managed</th>
            <th>On for new users</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const grantedId = `${idBase}-${r.id}-granted`;
            return (
              <tr key={r.id}>
                <td>
                  {r.mandatory
                    ? <span className="cell-primary">{r.name}</span>
                    : <label htmlFor={grantedId} className="cell-primary">{r.name}</label>}
                  {r.sub && <span className="cell-sub mono">{r.sub}</span>}
                  {r.badge}
                </td>
                <td>
                  {r.mandatory ? <span className="text-sm text-muted">Mandatory</span> : (
                    <Switch id={`${idBase}-${r.id}-managed`} checked={!!r.managed}
                            onChange={(on) => onManaged(r, on)}
                            disabled={locked || r.managed == null}
                            aria-label={`${r.name}: user managed for new users`} />
                  )}
                </td>
                <td>
                  {r.mandatory ? <span className="text-sm text-muted">Always on</span> : (
                    <Switch id={grantedId} checked={!!r.granted}
                            onChange={(on) => onGranted(r, on)}
                            disabled={locked || !!r.grantedLocked} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The ids whose value differs from what was loaded: what a save sends.
const changedIds = (draft, saved) => Object.keys(draft || {})
  .filter((id) => (saved || {})[id] !== draft[id]);

/* Defaults for new users of the platform's own tools, the last card on the External tab:
   the same rows, by the same names, as the user page's External tools tab. Platform Files'
   two tools are one row there and here. Speech and image handling are mandatory, listed so
   the table is the whole list. GET/PUT /api/tool-defaults/platform. */
function PlatformToolDefaults() {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const [view, setView] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [granted, setGranted] = useState({});
  const [managed, setManaged] = useState({});
  const [saving, setSaving] = useState(false);

  const savedGranted = Object.fromEntries((view?.tools || []).map((t) => [t.tool_id, t.default_granted]));
  const savedManaged = Object.fromEntries((view?.tools || [])
    .filter((t) => !t.mandatory).map((t) => [t.tool_id, t.default_user_managed]));
  const dirtyIds = new Set([
    ...changedIds(granted, savedGranted), ...changedIds(managed, savedManaged)]);
  const dirty = dirtyIds.size > 0;

  const adopt = (body) => {
    setView(body);
    setGranted(Object.fromEntries((body.tools || []).map((t) => [t.tool_id, t.default_granted])));
    setManaged(Object.fromEntries((body.tools || [])
      .filter((t) => !t.mandatory).map((t) => [t.tool_id, t.default_user_managed])));
  };

  const load = async () => {
    try {
      const resp = await apiFetch('/api/tool-defaults/platform');
      if (!resp.ok) { setLoadFailed(true); return; }
      adopt(await resp.json());
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A group (Platform Files) is one row whose switches move every member.
  const rows = [];
  const byGroup = {};
  for (const t of view?.tools || []) {
    const key = t.group || t.tool_id;
    if (byGroup[key]) { byGroup[key].members.push(t.tool_id); continue; }
    byGroup[key] = { id: key, name: t.group_name || t.name, sub: t.group ? null : t.tool_id,
                     mandatory: t.mandatory, members: [t.tool_id] };
    rows.push(byGroup[key]);
  }
  const shown = rows.map((r) => ({
    ...r,
    granted: r.members.every((m) => granted[m]),
    managed: r.members.every((m) => managed[m] != null) ? r.members.every((m) => managed[m]) : null,
  }));
  const setAll = (setter, members, on) => setter((prev) => ({
    ...prev, ...Object.fromEntries(members.map((m) => [m, on])) }));

  const save = async () => {
    setSaving(true);
    try {
      const tools = {};
      for (const id of dirtyIds) {
        tools[id] = {};
        if (granted[id] !== savedGranted[id]) tools[id].default_granted = granted[id];
        if (managed[id] !== savedManaged[id]) tools[id].user_managed = managed[id];
      }
      const resp = await apiFetch('/api/tool-defaults/platform', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tools }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(detailMessage(body.detail, 'Could not save the defaults'));
        return;
      }
      adopt(body);
      toast.success('New user defaults saved');
    } catch (e) {
      toast.error('Could not save the defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <SectionHead
        icon="users"
        title="Defaults for new users"
        tip={(
          <InfoTip label="About the defaults for new users">
            Read when a user is created, whichever way: the console, a coupon or an app. User
            managed lets the user&apos;s apps, such as RossHub, show the tool and switch it.
            Users that already exist keep what they have: change them on their user page.
          </InfoTip>
        )}
      />
      <p className="section-lede">
        What a new user starts with, per platform tool. Applies only to users created from now on.
      </p>
      {!view ? (loadFailed ? (
        <div className="notice notice-danger mb-0" role="alert">
          <Icon name="alertCircle" />
          <div className="notice-body row-wrap between gap-3">
            <span>Could not load the defaults.</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
              <Icon name="refresh" />
              Retry
            </button>
          </div>
        </div>
      ) : <CardSkeleton lines={4} />) : (
        <div className="stack gap-3">
          {!isSuper && (
            <div className="notice notice-info mb-0" role="status">
              <Icon name="lock" />
              <div className="notice-body">Read only: a superadmin can change these.</div>
            </div>
          )}
          <DefaultsTable
            rows={shown}
            locked={!isSuper || saving}
            onManaged={(r, on) => setAll(setManaged, r.members, on)}
            onGranted={(r, on) => setAll(setGranted, r.members, on)}
          />
          <div className="row-wrap between gap-3">
            <span className="text-sm text-faint row-wrap gap-2">
              {dirty && <Badge tone="warn">Unsaved changes</Badge>}
            </span>
            <div className="row-wrap gap-2">
              <span className="text-sm text-muted">Saving changes nothing for existing users.</span>
              <button type="button" className="btn btn-ghost" disabled={!dirty || saving}
                      onClick={() => adopt(view)}>
                Discard changes
              </button>
              <button type="button" className="btn btn-primary" disabled={!dirty || !isSuper || saving}
                      onClick={save}>
                {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Defaults for new users: which RossGate services every NEW user starts with.
   The rows are the same services, in the same order, as the user forms' RossGate tab:
   both come from `services/rossgate.py::offerable_services`, and the server refuses
   anything else. Saving changes nothing for existing users.
   Reloads whenever `defaults` changes, like RossGateConfig's status, so saving the
   gateway URL or key on the other tab updates the "not configured" notice. A reload
   never discards a selection the admin has not saved yet. */
function RossGateDefaults({ defaults }) {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const [view, setView] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  // User managed, per service: tool id -> whether a new user's apps may change it.
  const [managed, setManaged] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const savedIds = view?.selected || [];
  const managedOf = (body) => Object.fromEntries((body?.services || [])
    .filter((s) => s.user_managed != null).map((s) => [s.tool_id, s.user_managed]));
  const savedManaged = managedOf(view);
  const dirty = !!view && (selected.size !== savedIds.length
    || savedIds.some((id) => !selected.has(id))
    || changedIds(managed, savedManaged).length > 0);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const load = async () => {
    try {
      const resp = await apiFetch('/api/rossgate/defaults');
      if (!resp.ok) {
        setLoadFailed(true);
        return;
      }
      const body = await resp.json();
      setView(body);
      if (!dirtyRef.current) {
        setSelected(new Set(body.selected || []));
        setManaged(managedOf(body));
      }
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
    }
  };

  useEffect(() => { load(); }, [defaults]);

  const services = view?.services || [];
  const locked = !isSuper || saving;

  const toggle = (id, on) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      // In the list's own order, which is the order the server stores.
      const ids = services.map((s) => s.tool_id).filter((id) => selected.has(id));
      const managedChanges = Object.fromEntries(
        changedIds(managed, savedManaged).map((id) => [id, managed[id]]));
      const resp = await apiFetch('/api/rossgate/defaults', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ services: ids, user_managed: managedChanges }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        // The selection stays on screen, so nothing the admin picked is lost.
        toast.error(detailMessage(body.detail, 'Could not save the defaults'));
        return;
      }
      setView(body);
      setSelected(new Set(body.selected || []));
      setManaged(managedOf(body));
      toast.success('New user defaults saved');
    } catch (e) {
      toast.error('Could not save the defaults');
    } finally {
      setSaving(false);
    }
  };

  if (!view) {
    return loadFailed ? (
      <div className="notice notice-danger mb-0" role="alert">
        <Icon name="alertCircle" />
        <div className="notice-body row-wrap between gap-3">
          <span>Could not load the defaults.</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
            <Icon name="refresh" />
            Retry
          </button>
        </div>
      </div>
    ) : <CardSkeleton lines={4} />;
  }

  const unavailable = view.unavailable || [];
  const capMax = view.max_services_under_cap;

  return (
    <div className="stack gap-3">
      <p className="section-lede mb-0">
        The services every new user starts with, and which of them the user&apos;s apps can
        show and switch (User managed). Applies only to users created from now on.
      </p>

      {!isSuper && (
        <div className="notice notice-info mb-0" role="status">
          <Icon name="lock" />
          <div className="notice-body">Read only: a superadmin can change these.</div>
        </div>
      )}

      {!view.available ? (
        <div className="notice notice-info mb-0" role="status">
          <Icon name="info" />
          <div className="notice-body">RossGate is not offered on this environment.</div>
        </div>
      ) : (
        <>
          {!view.configured && (
            <div className="notice notice-warn mb-0" role="status">
              <Icon name="alert" />
              <div className="notice-body">
                Saved, but not applied: RossGate is not configured, so new users get no
                RossGate service until the gateway URL and master key are set.
              </div>
            </div>
          )}
          {unavailable.length > 0 && (
            <div className="notice notice-warn mb-0" role="status">
              <Icon name="alert" />
              <div className="notice-body">
                Saved but no longer offered, so not granted:{' '}
                <span className="mono">{unavailable.join(', ')}</span>. They are dropped the
                next time the set is saved.
              </div>
            </div>
          )}
          {capMax != null && selected.size > capMax && (
            <div className="notice notice-warn mb-0" role="status">
              <Icon name="alert" />
              <div className="notice-body">
                A grant cap of <span className="num">{view.grant_cap}</span> tools is set. A
                new user can receive about <span className="num">{capMax}</span> of the
                selected services; the rest are skipped.
              </div>
            </div>
          )}

          {view.catalogue_ok === false || services.length === 0 ? (
            <EmptyState icon="tools" title="No RossGate services available">
              This environment&apos;s catalogue has nothing in this group yet.
            </EmptyState>
          ) : (
            <>
              <div className="row-wrap between gap-3">
                <span className="text-sm text-muted" aria-live="polite">
                  <span className="num">{selected.size}</span> of{' '}
                  <span className="num">{services.length}</span> selected
                </span>
                <div className="row-wrap gap-2">
                  <button type="button" className="btn btn-ghost btn-sm" disabled={locked}
                          onClick={() => setSelected(new Set(services.map((s) => s.tool_id)))}>
                    Select all
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={locked}
                          onClick={() => setSelected(new Set())}>
                    Select none
                  </button>
                </div>
              </div>
              <DefaultsTable
                firstColumn="Service"
                locked={locked}
                rows={services.map((s) => ({
                  id: s.tool_id,
                  name: s.name,
                  sub: s.tool_id,
                  badge: s.min_tier_id != null && (
                    <Badge tone="neutral">From tier {s.min_tier_name || s.min_tier_id}</Badge>
                  ),
                  managed: s.tool_id in managed ? managed[s.tool_id] : null,
                  granted: selected.has(s.tool_id),
                }))}
                onManaged={(r, on) => setManaged((prev) => ({ ...prev, [r.id]: on }))}
                onGranted={(r, on) => toggle(r.id, on)}
              />
            </>
          )}

          <div className="row-wrap between gap-3">
            <span className="text-sm text-faint row-wrap gap-2">
              {view.updated_at ? `Updated ${formatDate(view.updated_at)}.` : 'Never saved.'}
              {dirty && <Badge tone="warn">Unsaved changes</Badge>}
            </span>
            <div className="row-wrap gap-2">
              <span className="text-sm text-muted">Saving changes nothing for existing users.</span>
              <button type="button" className="btn btn-ghost" disabled={!dirty || saving}
                      onClick={() => { setSelected(new Set(savedIds)); setManaged(savedManaged); }}>
                Discard changes
              </button>
              <button type="button" className="btn btn-primary" disabled={!dirty || !isSuper || saving}
                      onClick={save}>
                {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* RossGate — the external OAuth gateway behind Gmail, Jira, GitHub, Notion, …
   (Slack was here and is RETIRED, 2026-08-11: the gateway reports `configured: false`
   for it, so it cannot be connected. Do not re-add it to any copy on this page.)
   Two tabs. Gateway holds the two connection settings and the read-only list of
   what the platform has: which services exist is RossGate's decision (synced into
   the tool catalogue). Defaults for new users decides which of them a NEW user
   starts with. Which ones an existing user has stays that user's (and their admin's
   on the user form); this page never changes it. */
function RossGateConfig({ defaults, handleSave, saving }) {
  const [tab, setTab] = useState('gateway');
  const [status, setStatus] = useState(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const resp = await apiFetch('/api/rossgate/status');
      if (resp.ok) {
        setStatus(await resp.json());
        setStatusFailed(false);
      } else {
        setStatusFailed(true);
      }
    } catch (e) {
      /* the card still renders its config fields */
      setStatusFailed(true);
    }
  };

  useEffect(() => { load(); }, [defaults]);

  const run = async (action, label) => {
    setBusy(action);
    try {
      const resp = await apiFetch(`/api/rossgate/${action}`, { method: 'POST' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(body.detail || `${label} failed`);
      } else if (body.ok === false) {
        // A reachable-but-unhappy gateway reports itself in the payload rather
        // than by status code; surface its own words.
        toast.error(body.detail || body.reason || `${label} failed`);
      } else {
        toast.success(body.detail || `${label} complete`);
      }
      load();
    } catch (e) {
      toast.error(`${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const services = (status?.services || []).filter(s => !s.control);
  const lastSync = status?.last_sync_at
    ? new Date(status.last_sync_at).toLocaleString()
    : null;
  const warnings = status?.last_sync_summary?.warnings || [];
  // Both halves stored and still "not configured" has exactly one cause in
  // `services/rossgate.py::configured`: this environment still runs Maton. Without
  // saying so, the badge contradicts the two fields and sends the admin to re-enter
  // keys that are already correct.
  const onMaton = !!status && !status.configured && status.base_url_set && status.master_key_set;

  const badge = status
    ? (status.configured
      ? <Badge tone="ok" dot>Configured</Badge>
      : <Badge tone="warn" dot>Not configured</Badge>)
    : <Badge tone="neutral">{statusFailed ? 'Status unavailable' : 'Checking…'}</Badge>;

  return (
    <div className="card">
      <SectionHead
        icon="link"
        title="RossGate"
        status={badge}
        tip={(
          <InfoTip label="About RossGate" width={360}>
            The platform mints a key for each user from the master key; agents never see
            either. New users start with the services under Defaults for new users and enable
            the rest from their own tool list, and each works once its user has authorised it.
            An existing user's services change on their user page, never here.
          </InfoTip>
        )}
      />
      <div className="mb-4">
        <Tabs variant="line" label="RossGate settings" idPrefix="rg" value={tab} onChange={setTab}
              tabs={[
                { value: 'gateway', label: 'Gateway' },
                { value: 'defaults', label: 'Defaults for new users' },
              ]} />
      </div>

      {/* Both panels stay mounted, so unsaved switches survive a tab change. */}
      <div role="tabpanel" id="rg-panel-gateway" aria-labelledby="rg-tab-gateway"
           hidden={tab !== 'gateway'}>
        {/* ⚠ SLACK USED TO BE NAMED HERE AND IS RETIRED (2026-08-11) — the gateway reports
            `configured: false` for it, so it cannot be connected at all. Naming a dead
            service sent operators looking for a toggle that does not exist. The live list is
            whatever the Services table below shows, which is synced; this sentence names only
            the ones stable enough to be worth naming. */}
        <p className="section-lede">
          Each agent's way into its user's own Gmail, Calendar, Drive, Jira, GitHub, Notion
          and Dropbox.
        </p>

        <div className="form-row">
          <ConfigField
            label="Gateway URL"
            description="No trailing path."
            configKey="rossgate_base_url"
            defaults={defaults}
            onSave={handleSave}
            saving={saving.rossgate_base_url}
            placeholder="https://rossgate.internal"
          />
          <ConfigField
            label="Master API key"
            description="Mints per-user keys. Treat as a root credential."
            configKey="rossgate_master_key"
            secret
            defaults={defaults}
            onSave={handleSave}
            saving={saving.rossgate_master_key}
            placeholder="Enter RossGate master key…"
          />
        </div>

        {onMaton && (
          <div className="notice notice-info">
            <Icon name="info" />
            <div className="notice-body">
              Both values are set, but this environment is still on Maton, which keeps
              RossGate off whatever is entered here.
            </div>
          </div>
        )}

        {/* Status + actions */}
        <div className="card-inset">
          <div className="row-wrap between gap-3">
            <div className="stack gap-1 min-w-0">
              {status?.configured ? (
                <span className="text-sm">
                  <span className="num">{services.length}</span> service{services.length === 1 ? '' : 's'}
                  {' · '}
                  <span className="num">{status.users_with_key}</span> active key{status.users_with_key === 1 ? '' : 's'}
                  {/* Keys are minted at a user's first boot holding a service, so a user with
                      none is normal, not a gap: the counts that matter are the queue. */}
                  {(status.keys?.pending || 0) + (status.keys?.minting || 0) > 0 && (
                    <> · <span className="num">{(status.keys.pending || 0) + (status.keys.minting || 0)}</span> being minted</>
                  )}
                  {(status.keys?.failed || 0) > 0 && (
                    <> · <span className="num text-danger">{status.keys.failed}</span> failed</>
                  )}
                </span>
              ) : (
                <span className="text-sm text-muted">Test and sync unlock once the gateway is configured.</span>
              )}
              {lastSync && (
                <span className="text-sm text-faint">
                  Last synced {lastSync}
                  {status?.last_sync_summary?.remote_catalogue === false && (
                    <> · using the built-in service list (RossGate publishes no catalogue yet)</>
                  )}
                </span>
              )}
            </div>
            <div className="row-wrap gap-2">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => run('test', 'Connection test')}
                disabled={!status?.configured || busy !== null}
              >
                {busy === 'test' ? <span className="spinner-sm" /> : <Icon name="activity" />}
                {busy === 'test' ? 'Testing…' : 'Test connection'}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => run('sync', 'Sync')}
                disabled={!status?.configured || busy !== null}
              >
                {busy === 'sync' ? <span className="spinner-sm" /> : <Icon name="refresh" />}
                {busy === 'sync' ? 'Syncing…' : 'Sync services'}
              </button>
            </div>
          </div>
        </div>

        {status?.last_sync_error && (
          <div className="notice notice-danger mt-3 mb-0">
            <Icon name="alertCircle" />
            <div className="notice-body">Last sync failed: {status.last_sync_error}</div>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="notice notice-warn mt-3 mb-0">
            <Icon name="alert" />
            <div className="notice-body stack gap-1">
              {warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          </div>
        )}

        {services.length > 0 && (
          <Disclosure label="Services available to users" count={services.length}>
            <p className="text-sm text-muted mb-3">
              Synced from RossGate. Users enable these from their own tool list. Pick the ones
              new users start with under Defaults for new users.
            </p>
            <div className="table-container bordered">
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th className="num">Users</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map(s => (
                    <tr key={s.tool_id}>
                      <td>
                        <span className="cell-primary">{s.name}</span>
                        <span className="cell-sub mono">{s.tool_id}</span>
                      </td>
                      <td className="num">{s.granted_users}</td>
                      <td>
                        <Badge tone={s.lifecycle === 'active' ? 'ok' : 'warn'} dot>
                          {s.lifecycle
                            ? s.lifecycle.charAt(0).toUpperCase() + s.lifecycle.slice(1)
                            : 'Unknown'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Disclosure>
        )}
      </div>

      <div role="tabpanel" id="rg-panel-defaults" aria-labelledby="rg-tab-defaults"
           hidden={tab !== 'defaults'}>
        <RossGateDefaults defaults={defaults} />
      </div>
    </div>
  );
}

/* Third-party MCP servers (backend/mcpgate) — Syrto today.

   Different in kind from RossGate above, and the layout says so. RossGate is one
   credential per USER, so its card is about provisioning people. An MCP server is ONE
   platform credential shared by everybody who holds the tool, so this card is about the
   credential, the sub-tools it exposes, and who gets the tool at all.

   The sub-tool list is NOT hardcoded here. It is whatever the server reported on the last
   sync (`services/mcpgate.py::discover`), which is the point: a tool the vendor adds
   appears here on its own. A tool on the platform denylist is shown too, marked blocked —
   hiding it would send an operator looking for a bug when they cannot find a tool the
   vendor's own docs describe.

   Each server has four tabs (McpServerTabs, below): Config, the Description its holders'
   prompts carry, and the Instruction and Tools that `describe_mcp_server` returns. */
function McpServersConfig({ defaults, handleSave, saving }) {
  const [status, setStatus] = useState(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [interval, setIntervalValue] = useState('');
  const intervalId = useId();
  // The hook returns a PAIR and the dialog has to be rendered: destructure only the
  // first half and `confirm()` returns a promise that never resolves, so the handler
  // stops at the await with no dialog and no error. See
  // tests/test_frontend_copy_standard.py::test_every_use_confirm_renders_its_dialog.
  const [confirm, confirmDialog] = useConfirm();

  const load = async () => {
    try {
      const resp = await apiFetch('/api/mcp/status');
      if (resp.ok) {
        const body = await resp.json();
        setStatus(body);
        setIntervalValue(String(body.sync_interval_minutes ?? ''));
        setStatusFailed(false);
      } else {
        setStatusFailed(true);
      }
    } catch (e) {
      /* the card still renders its credential fields */
      setStatusFailed(true);
    }
  };

  useEffect(() => { load(); }, [defaults]);

  const run = async (action, label) => {
    setBusy(action);
    try {
      const resp = await apiFetch(`/api/mcp/${action}`, { method: 'POST' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(detailMessage(body.detail, `${label} failed`));
      } else if (body.ok === false) {
        // A reachable-but-unhappy server reports itself in the payload rather than
        // by status code; surface its own words.
        toast.error(body.detail || `${label} failed`);
      } else {
        toast.success(body.detail || `${label} complete`);
      }
      load();
    } catch (e) {
      toast.error(`${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const saveInterval = async () => {
    setBusy('schedule');
    try {
      const resp = await apiFetch('/api/mcp/schedule', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes: Number(interval) }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(detailMessage(body.detail, 'Could not save the interval'));
      } else {
        toast.success(body.detail || 'Interval saved');
      }
      load();
    } catch (e) {
      toast.error('Could not save the interval');
    } finally {
      setBusy(null);
    }
  };

  const servers = status?.servers || [];
  const pending = status?.pending || [];
  const warnings = status?.last_sync_summary?.warnings || [];
  const lastSync = status?.last_sync_at
    ? new Date(status.last_sync_at).toLocaleString()
    : null;
  const bounds = status?.sync_interval_bounds || { min: 10, max: 10080 };
  const anyCredential = servers.some(s => s.credential_set)
    || pending.some(p => p.credential_set);

  // `enabled` is absent on an older backend, so only an EXPLICIT false hides the
  // section — the same "treat only false as off" rule the RossGate tab uses. A
  // version skew or a failed status call therefore leaves the section visible
  // rather than making a working feature disappear.
  //
  // The same for an environment that is on but lists no server (`mcp_servers` empty in
  // its infra, PROD since 2026-11-03): the status answers with no server and nothing
  // pending, and an empty section would be the only trace of MCP on the console. Both
  // lists have to be present and empty, so an older or a failed answer still shows it.
  const nothingListed = status != null
    && Array.isArray(status.servers) && Array.isArray(status.pending)
    && status.servers.length === 0 && status.pending.length === 0;
  const featureOff = (status != null && status.enabled === false) || nothingListed;

  const badge = status
    ? (anyCredential
      ? <Badge tone="ok" dot>Configured</Badge>
      : <Badge tone="warn" dot>Not configured</Badge>)
    : <Badge tone="neutral">{statusFailed ? 'Status unavailable' : 'Checking…'}</Badge>;

  // ⚠ GONE, not grayed out. An environment that has not opted in shows no MCP
  // section at all, which is a deliberate departure from the Load tests page (it
  // renders grayed and explains itself through GATED_PAGES). The trade is known and
  // accepted: nothing here hints that the feature exists, so an operator who does
  // not know about `mcp_servers_enabled` will read its absence as "not deployed".
  // The handbook's Operate the platform page is where that is written down.
  //
  // Rendered as an early return rather than a condition on each control, so every
  // button, field and switch below is unreachable by construction instead of
  // individually disabled — there is no path that can forget one.
  if (featureOff) return null;

  return (
    <div className="card">
      <SectionHead
        icon="server"
        title="MCP servers"
        status={badge}
        tip={(
          <InfoTip label="About MCP servers" width={360}>
            <strong>Off for everyone by default.</strong> Store the key, sync, then give the
            server to new users here, or to an existing user from their user page. The
            platform holds one credential per server, shared by every user who has the tool
            together with its allowance and rate limit.
          </InfoTip>
        )}
      />
      <p className="section-lede">
        Third-party Model Context Protocol servers. Each is one tool for the agent, with the
        server's own tools under it.
      </p>

      {/* The key of a server not discovered yet: it has no card of its own until its first
          sync, and that sync needs the key. A discovered server's key is on its Config tab.
          Keyed off the descriptor, so a server added in code appears without this file
          changing. */}
      {pending.length > 0 && (
        <div className="form-row">
          {pending.map(s => (
            <ConfigField
              key={s.credential_key}
              label={`${s.name} API key`}
              description="Organization key. Shared by every user who has the tool."
              configKey={s.credential_key}
              secret
              defaults={defaults}
              onSave={handleSave}
              saving={saving[s.credential_key]}
              placeholder={`Enter ${s.name} organization key…`}
            />
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="notice notice-info">
          <Icon name="info" />
          <div className="notice-body">
            {pending.map(p => p.name).join(', ')} {pending.length === 1 ? 'has' : 'have'} not
            been discovered yet: store the API key above, then press Sync tools. The tool list
            comes from the server itself.
          </div>
        </div>
      )}

      {/* Status + actions */}
      <div className="card-inset">
        <div className="row-wrap between gap-3">
          <div className="stack gap-1 min-w-0">
            {servers.length > 0 ? (
              <span className="text-sm">
                <span className="num">{servers.length}</span> server
                {servers.length === 1 ? '' : 's'}
                {' · '}
                <span className="num">
                  {servers.reduce((n, s) => n + s.exposed_count, 0)}
                </span> tool
                {servers.reduce((n, s) => n + s.exposed_count, 0) === 1 ? '' : 's'} exposed
              </span>
            ) : (
              <span className="text-sm text-muted">
                Sync unlocks once a credential is stored.
              </span>
            )}
            <span className="text-sm text-faint">
              {lastSync ? <>Last synced {lastSync}</> : <>Never synced</>}
              {status?.next_sync_due === false && <> · next automatic sync not due yet</>}
            </span>
          </div>
          <div className="row-wrap gap-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => run('test', 'Connection test')}
              disabled={!anyCredential || busy !== null}
            >
              {busy === 'test' ? <span className="spinner-sm" /> : <Icon name="activity" />}
              {busy === 'test' ? 'Testing…' : 'Test connection'}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => run('sync', 'Sync')}
              disabled={!anyCredential || busy !== null}
            >
              {busy === 'sync' ? <span className="spinner-sm" /> : <Icon name="refresh" />}
              {busy === 'sync' ? 'Syncing…' : 'Sync tools'}
            </button>
          </div>
        </div>
      </div>

      {/* The schedule. Says plainly that it throttles rather than reschedules, because an
          admin who reads it as "run every 6 hours" would expect a sync 6 hours after
          saving, and what they get is "no more often than every 6 hours". */}
      <div className="card-inset mt-3">
        <div className="row-wrap between gap-3 items-end">
          <div className="stack gap-1 min-w-0">
            <span className="text-sm row gap-1">
              Automatic sync
              <InfoTip label="About automatic sync">
                A throttle, not a timer: the scheduled job runs often and syncs only once this
                long has passed since the last successful sync. Sync tools runs at once
                regardless.
              </InfoTip>
            </span>
            <span className="text-sm text-faint">Minimum time between automatic syncs.</span>
          </div>
          <div className="row-wrap gap-2 items-end">
            {/* `form-input`: the `input` class it had exists nowhere, so the field drew as
                the browser's bare default. */}
            <Field label="Minutes" htmlFor={intervalId} className="w-28">
              <input
                id={intervalId}
                type="number"
                className="form-input"
                min={bounds.min}
                max={bounds.max}
                value={interval}
                onChange={(e) => setIntervalValue(e.target.value)}
              />
            </Field>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={saveInterval}
              disabled={busy !== null
                || interval === ''
                || Number(interval) === status?.sync_interval_minutes}
            >
              {busy === 'schedule' ? <span className="spinner-sm" /> : <Icon name="check" />}
              Save
            </button>
          </div>
        </div>
      </div>

      {status?.last_sync_error && (
        <div className="notice notice-danger mt-3 mb-0">
          <Icon name="alertCircle" />
          <div className="notice-body">Last sync failed: {status.last_sync_error}</div>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="notice notice-warn mt-3 mb-0">
          <Icon name="alert" />
          <div className="notice-body stack gap-1">
            {warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        </div>
      )}

      {/* Per server: its name, then its four tabs at once (Mark, 2026-10-23). */}
      {servers.map(s => (
        <div key={s.tool_id} className="card-inset mt-3">
          <span className="cell-primary">
            {s.name} <span className="cell-sub mono">{s.tool_id}</span>
          </span>
          <McpServerTabs
            server={s}
            limits={{
              description: status?.description_max_chars || 300,
              instruction: status?.instruction_max_chars || 4000,
              rate: status?.max_rate_limit_per_hour || 5000,
            }}
            onSaved={load}
            defaults={defaults}
            handleSave={handleSave}
            saving={saving}
          />
        </div>
      ))}

      {servers.length > 0 && (
        <McpServerDefaults servers={servers} confirm={confirm} onSaved={load} />
      )}

      {servers.length === 0 && pending.length === 0 && !statusFailed && (
        <EmptyState icon="server" title="No MCP servers configured">
          None are defined for this environment.
        </EmptyState>
      )}

      {confirmDialog}
    </div>
  );
}

/* One MCP server's four tabs (Mark, 2026-10-23), right under its name:

   - Config: its key, its counts and address, and its default hourly cap. Whether new
     users get it moved to the section's Defaults for new users table (2026-11-02).
   - Description: the line every holder's prompt carries for it, which is what the agent
     reads when deciding WHETHER to use the server, on every turn.
   - Instruction and Tools: together exactly what `describe_mcp_server` returns, once the
     agent has decided: the Instruction, then the Tools with the summaries the server gave
     on the last sync.

   Every panel stays mounted, so an unsaved draft survives a look at another tab. Defined
   below McpServersConfig, inside the slice tests/test_mcpgate_flag.py reads, so its controls
   stay behind the card's feature gate. */
function McpServerTabs({ server: s, limits, onSaved, defaults, handleSave, saving }) {
  const [view, setView] = useState('config');
  const prefix = `mcp-${s.tool_id}`;
  return (
    <>
      <div className="mt-3 mb-3">
        <Tabs variant="line" label={`${s.name} settings`} idPrefix={prefix} value={view}
              onChange={setView}
              tabs={[
                { value: 'config', label: 'Config' },
                { value: 'description', label: 'Description' },
                { value: 'instruction', label: 'Instruction' },
                { value: 'tools', label: 'Tools', count: s.tools.length },
              ]} />
      </div>

      <div role="tabpanel" id={`${prefix}-panel-config`} aria-labelledby={`${prefix}-tab-config`}
           hidden={view !== 'config'}>
        <div className="stack gap-3">
          <div className="stack gap-1">
            <span className="text-sm text-faint">
              <span className="num">{s.exposed_count}</span> tool
              {s.exposed_count === 1 ? '' : 's'} exposed
              {s.denied.length > 0 && <> · <span className="num">{s.denied.length}</span> blocked</>}
              {' · '}
              <span className="num">{s.granted_users}</span> user
              {s.granted_users === 1 ? '' : 's'} have it
              {s.server_url && <> · <span className="mono">{s.server_url}</span></>}
              {s.protocol === 'api' && (
                <> · <span title="A REST API: its tools are translated from its OpenAPI document at each sync">
                  REST API (OpenAPI)</span></>
              )}
            </span>
            {!s.credential_set && (
              <span className="text-sm text-warn">
                No credential stored: nobody can use it, and a user granted it does not even
                see it until the key is set.
              </span>
            )}
            {s.auto_discover === false && (
              <span className="text-sm text-faint">
                Auto-discovery off: a sync reports new tools without exposing them.
              </span>
            )}
          </div>
          <div className="form-row mb-0">
            <ConfigField
              label={`${s.name} API key`}
              description="Organization key. Shared by every user who has the tool."
              configKey={s.credential_key}
              secret
              defaults={defaults}
              onSave={handleSave}
              saving={saving[s.credential_key]}
              placeholder={`Enter ${s.name} organization key…`}
            />
          </div>
          <McpRateLimitDefault server={s} maxRate={limits.rate} onSaved={onSaved} />
        </div>
      </div>

      <div role="tabpanel" id={`${prefix}-panel-description`}
           aria-labelledby={`${prefix}-tab-description`} hidden={view !== 'description'}>
        <McpDescriptionEditor server={s} maxChars={limits.description} onSaved={onSaved} />
      </div>

      <div role="tabpanel" id={`${prefix}-panel-instruction`}
           aria-labelledby={`${prefix}-tab-instruction`} hidden={view !== 'instruction'}>
        <McpInstructionEditor server={s} maxChars={limits.instruction} onSaved={onSaved} />
      </div>

      <div role="tabpanel" id={`${prefix}-panel-tools`} aria-labelledby={`${prefix}-tab-tools`}
           hidden={view !== 'tools'}>
        {(s.last_added?.length > 0 || s.last_removed?.length > 0) && (
          <div className="notice notice-info mb-3">
            <Icon name="info" />
            <div className="notice-body stack gap-1">
              {s.last_added?.length > 0 && (
                <div>Last sync added: {s.last_added.join(', ')}</div>
              )}
              {s.last_removed?.length > 0 && (
                <div>Last sync removed: {s.last_removed.join(', ')}</div>
              )}
            </div>
          </div>
        )}

        {s.tools.length > 0 ? (
          <>
            <p className="text-sm text-muted mb-3">
              Reported by the server itself on the last sync, with the summaries the agent
              reads after the instruction. The agent reaches these through
              {' '}<code>{s.tool_id}</code>; it cannot call a blocked one.
            </p>
            <div className="table-container bordered">
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>What it does</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {s.tools.map(t => (
                    <tr key={t.name}>
                      <td><span className="cell-primary mono">{t.name}</span></td>
                      <td>
                        {t.summary
                          ? <span className="text-sm text-muted">{t.summary}</span>
                          : <span className="text-sm text-faint">No description</span>}
                      </td>
                      <td>
                        {t.exposed
                          ? <Badge tone="ok" dot>Available</Badge>
                          : <Badge tone="danger" dot>Blocked</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {s.denied.length > 0 && (
              <p className="text-sm text-faint mt-2">
                Blocked tools are on the platform denylist in
                {' '}<span className="mono">backend/mcpgate/servers.py</span>: they spend
                credits, so exposing one is a code change.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted">No tools yet: they arrive with the next sync.</p>
        )}
      </div>
    </>
  );
}

/* The MCP servers' Defaults for new users table (Mark, 2026-11-02): per server, User managed
   and On for new users, saved together by PUT /api/mcp/defaults, read when a user is
   created and never applied to one that exists. It replaced each Config tab's "Give to new
   users" switch.

   Turning a server ON for new users is a spend decision, so the save asks first: every new
   user's agent gets a tool that bills a shared allowance, and the person clicking cannot
   see that from a switch. Turning it off needs no confirmation: it takes nothing away from
   anyone who already has it. A server with no key stored cannot be turned on, as before.
   Defined below McpServersConfig, so it stays behind the card's feature gate. */
function McpServerDefaults({ servers, confirm, onSaved }) {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const savedGranted = Object.fromEntries(servers.map((s) => [s.tool_id, !!s.default_granted]));
  const savedManaged = Object.fromEntries(servers.map((s) => [
    s.tool_id, s.default_user_managed == null ? null : !!s.default_user_managed]));
  const [granted, setGranted] = useState(savedGranted);
  const [managed, setManaged] = useState(savedManaged);
  const [saving, setSaving] = useState(false);
  // The section reloads its status after every action on it. A draft survives that unless
  // the saved values themselves changed.
  const savedKey = JSON.stringify([savedGranted, savedManaged]);
  useEffect(() => {
    setGranted(savedGranted);
    setManaged(savedManaged);
  }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirtyIds = new Set([
    ...changedIds(granted, savedGranted), ...changedIds(managed, savedManaged)]);
  const dirty = dirtyIds.size > 0;

  const save = async () => {
    const turnedOn = servers.filter((s) => granted[s.tool_id] && !savedGranted[s.tool_id]);
    if (turnedOn.length > 0) {
      const names = turnedOn.map((s) => s.name).join(', ');
      const ok = await confirm({
        title: `Give ${names} to every new user?`,
        body: (
          <>
            Every new user's agent will be able to call {names}, which bills the
            platform's shared allowance and rate limit. Existing users are not affected:
            grant it to them from their user page.
          </>
        ),
        confirmLabel: 'Enable for new users',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const changes = {};
      for (const id of dirtyIds) {
        changes[id] = {};
        if (granted[id] !== savedGranted[id]) changes[id].default_granted = granted[id];
        if (managed[id] !== savedManaged[id]) changes[id].user_managed = managed[id];
      }
      const resp = await apiFetch('/api/mcp/defaults', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ servers: changes }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(detailMessage(body.detail, 'Could not save the defaults'));
        return;
      }
      toast.success(body.detail || 'New user defaults saved');
      onSaved();
    } catch (e) {
      toast.error('Could not save the defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-inset mt-3">
      <div className="stack gap-3">
        <div className="stack gap-1 min-w-0">
          <span className="cell-primary">Defaults for new users</span>
          <span className="text-sm text-faint">
            What a new user starts with, per server. Applies only to users created from now on.
          </span>
        </div>
        {!isSuper && (
          <div className="notice notice-info mb-0" role="status">
            <Icon name="lock" />
            <div className="notice-body">Read only: a superadmin can change these.</div>
          </div>
        )}
        <DefaultsTable
          firstColumn="Server"
          locked={!isSuper || saving}
          rows={servers.map((s) => ({
            id: s.tool_id,
            name: s.name,
            sub: s.tool_id,
            managed: managed[s.tool_id],
            granted: granted[s.tool_id],
            grantedLocked: !s.credential_set && !granted[s.tool_id],
          }))}
          onManaged={(r, on) => setManaged((prev) => ({ ...prev, [r.id]: on }))}
          onGranted={(r, on) => setGranted((prev) => ({ ...prev, [r.id]: on }))}
        />
        <div className="row-wrap between gap-3">
          <span className="text-sm text-faint row-wrap gap-2">
            {dirty && <Badge tone="warn">Unsaved changes</Badge>}
          </span>
          <div className="row-wrap gap-2">
            <span className="text-sm text-muted">Saving changes nothing for existing users.</span>
            <button type="button" className="btn btn-ghost" disabled={!dirty || saving}
                    onClick={() => { setGranted(savedGranted); setManaged(savedManaged); }}>
              Discard changes
            </button>
            <button type="button" className="btn btn-primary" disabled={!dirty || !isSuper || saving}
                    onClick={save}>
              {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* The server's DEFAULT hourly cap: what every holder without a cap of their own is
   limited to. The per-user override is the same number on the user's own page
   (components/McpRateLimitCell.jsx); this is the policy, that is the exception.

   ⚠ It used to be in code, in TWO images — `McpServer.default_rate_limit_per_hour` for the
   console to display and `RATE_LIMIT_BY_TOOL` in proxy-tools to enforce, held equal by a
   test — so changing a shared vendor budget meant a deploy of both (Mark, 2026-10-28). It
   is `tool_catalogue.default_rate_limit_per_hour` now, read by the catalogue join
   proxy-tools' grant store already does.

   RAISING it asks first, for the reason turning a server on for new users does
   (McpServerDefaults): the vendor's allowance is
   shared by the whole organization, so a higher per-user cap lets every holder take a bigger
   share of the same pool, and the person typing a number cannot see that. Lowering needs no
   confirmation — it takes a budget back rather than committing one. */
function McpRateLimitDefault({ server: s, maxRate, onSaved }) {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const inputId = useId();
  const hintId = useId();
  const [confirm, confirmDialog] = useConfirm();
  const saved = s.default_rate_limit_per_hour;
  const [draft, setDraft] = useState(saved == null ? '' : String(saved));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(saved == null ? '' : String(saved)); }, [saved]);

  const trimmed = draft.trim();
  const value = Number(trimmed);
  const valid = trimmed !== '' && Number.isInteger(value) && value >= 1 && value <= maxRate;
  const changes = trimmed !== '' && value !== saved;

  const save = async () => {
    if (saved != null && value > saved && !await confirm({
      title: `Raise ${s.name}'s cap to ${value} calls an hour?`,
      body: (
        <>
          Every user who holds {s.name} and has no cap of their own goes from{' '}
          {saved} to {value} calls an hour. The vendor's allowance is shared by the whole
          organization, so one user at the ceiling leaves less for the rest.
        </>
      ),
      confirmLabel: `Raise to ${value}`,
    })) return;
    setSaving(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/mcp/servers/${s.tool_id}/rate-limit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate_limit_per_hour: value }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(detailMessage(body.detail, 'Could not save the cap'));
      toast.success(body.detail || 'Saved');
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="setting-row flush">
      <div className="grow min-w-0">
        <div className="setting-row-title row-wrap gap-2">
          <label className="mb-0" htmlFor={inputId}>Calls an hour, per user</label>
          <InfoTip label={`About ${s.name}'s hourly cap`} width={400}>
            <p>
              A user's call is refused once they have made this many in the last hour. Per
              user, not per platform: the vendor's allowance is shared, so this bounds what
              ONE user takes of it, not the total.
            </p>
            <p>
              In order: the user's own cap if one is set on their page, then this, then the
              backstop of {maxRate}. A change reaches every holder without their own cap
              within seconds, with no deploy.
            </p>
          </InfoTip>
        </div>
        <div className="setting-row-desc" id={hintId}>
          Every user who has {s.name} and no cap of their own.
          {s.users_with_an_override > 0 && (
            <> {s.users_with_an_override} user
              {s.users_with_an_override === 1 ? ' has' : 's have'} their own and
              {' '}{s.users_with_an_override === 1 ? 'is' : 'are'} unaffected.</>
          )}
          {' '}From 1 to {maxRate}
          {s.shipped_rate_limit_per_hour != null
            && <>, and {s.name} ships with {s.shipped_rate_limit_per_hour}</>}.
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
      </div>
      {/* The same track as a phone-call setting, two columns wide: one number and one
          Save, at the widths the Timings tab uses, so the console has one shape for
          "a number with its own Save" rather than two. */}
      <div className="timing-control pair">
        <input
          id={inputId}
          type="number"
          inputMode="numeric"
          className="form-input num"
          min={1}
          max={maxRate}
          step={1}
          value={draft}
          aria-describedby={hintId}
          aria-invalid={trimmed !== '' && !valid ? true : undefined}
          disabled={!isSuper || saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter saves THIS value rather than submitting anything around it.
            if (e.key === 'Enter') { e.preventDefault(); if (valid && changes && !saving) save(); }
            if (e.key === 'Escape') setDraft(saved == null ? '' : String(saved));
          }}
        />
        <button type="button" className="btn btn-primary" onClick={save}
                disabled={!isSuper || saving || !valid || !changes}>
          {saving ? <span className="spinner-sm" /> : 'Save'}
        </button>
      </div>
      {confirmDialog}
    </div>
  );
}

/* The description: the one line the agent's system prompt carries for the server, under
   "MCP servers", and in call_mcp_tool's list of tools. What the agent reads when deciding
   whether to use the server, so: when to use it.

   Seeded from the code's descriptor by the sync that created the row, and the console's
   since; "Use the default" puts the code's text back into the draft. A draft for the same
   reason as the instruction. The backend stores it as one line, so the count and the
   limit are measured on that line, and Save stays off while saving would change nothing. */
function McpDescriptionEditor({ server: s, maxChars, onSaved }) {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const textareaId = useId();
  const hintId = useId();
  const saved = s.description || '';
  const fallback = s.default_description || '';
  const [draft, setDraft] = useState(saved);
  const [baseline, setBaseline] = useState(saved);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (saved !== baseline) {
      setBaseline(saved);
      setDraft(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const oneLine = draft.split(/\s+/).filter(Boolean).join(' ');
  const changes = oneLine !== baseline;
  const length = oneLine.length;
  const tooLong = length > maxChars;
  const isDefault = !!fallback && baseline === fallback;

  const save = async () => {
    setSaving(true);
    try {
      const resp = await apiFetch(`/api/mcp/servers/${s.tool_id}/description`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: draft }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(detailMessage(body.detail, 'Could not save the description'));
      } else {
        const stored = body.description ?? oneLine;
        setBaseline(stored);
        setDraft(stored);
        toast.success(body.detail || 'Description saved');
        onSaved();
      }
    } catch (e) {
      toast.error('Could not save the description');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack gap-3">
      <div className="form-group mb-0">
        <div className="row-wrap gap-2 mb-2">
          <label className="form-label mb-0" htmlFor={textareaId}>Description</label>
          <InfoTip label={`About the ${s.name} description`}>
            The one line about {s.name} in the system prompt of every user who has it, under MCP
            servers, and in call_mcp_tool&apos;s list of tools. The agent reads it on
            every turn to decide whether to use {s.name}, so say when to use it. Agents take a
            change at their next turn.
          </InfoTip>
          {fallback && (
            <Badge tone={isDefault ? 'neutral' : 'info'}>{isDefault ? 'Default' : 'Customised'}</Badge>
          )}
          {changes && <Badge tone="warn" dot className="ml-auto">Unsaved changes</Badge>}
        </div>
        <textarea
          id={textareaId}
          className="form-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          readOnly={!isSuper}
          aria-describedby={hintId}
          aria-invalid={tooLong || length === 0 || undefined}
          rows={3}
        />
        <div id={hintId} className="row-wrap between gap-2 mt-1">
          <span className="text-sm text-faint">
            {isSuper
              ? 'One line: line breaks are joined. It cannot be empty.'
              : 'Only a superadmin can change it.'}
          </span>
          <span className={cx('text-sm num', tooLong ? 'text-danger' : 'text-faint')}>
            {length.toLocaleString()} / {maxChars.toLocaleString()} characters
          </span>
        </div>
      </div>

      {isSuper && (
        <div className="row-wrap gap-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={save}
                  disabled={saving || !changes || tooLong || length === 0}>
            {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save description'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setDraft(baseline)} disabled={saving || draft === baseline}>
            Discard
          </button>
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setDraft(fallback)}
                  disabled={saving || !fallback || oneLine === fallback}>
            Use the default
          </button>
        </div>
      )}

      {fallback && !isDefault && (
        <p className="text-sm text-faint mb-0">
          The default, from the code: {fallback}
        </p>
      )}
    </div>
  );
}

/* The instruction: the first part of what `describe_mcp_server` returns, a short
   introduction to what the server is for. The tool list follows it on its own.

   A draft, like CorrectionPromptEditor and for the same reason: a textarea cannot save
   per keystroke, and the status reload after any action on this card must not wipe what
   the admin is typing. So the draft is re-seeded only when the SAVED instruction changes,
   and a save adopts the stored text (trimmed by the backend) as the new baseline.

   Beside it, read-only: what the agent gets now (the stored document, not a client-side
   imitation of the renderer, which could drift), and the server's own notes from its
   last sync. Those are no longer sent; an admin who wants any of them sent puts them in
   the instruction. Only a superadmin can save, as on the other editors of this page. */
function McpInstructionEditor({ server: s, maxChars, onSaved }) {
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const textareaId = useId();
  const hintId = useId();
  const saved = s.instruction || '';
  const [draft, setDraft] = useState(saved);
  const [baseline, setBaseline] = useState(saved);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (saved !== baseline) {
      setBaseline(saved);
      setDraft(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const dirty = draft !== baseline;
  const length = draft.trim().length;
  const tooLong = length > maxChars;
  const unavailable = s.instruction_unavailable_tools || [];
  const notesUnavailable = s.server_notes_unavailable_tools || [];

  const save = async () => {
    setSaving(true);
    try {
      const resp = await apiFetch(`/api/mcp/servers/${s.tool_id}/instruction`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: draft }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(detailMessage(body.detail, 'Could not save the instruction'));
      } else {
        const stored = body.instruction ?? draft.trim();
        setBaseline(stored);
        setDraft(stored);
        toast.success(body.detail || 'Instruction saved');
        onSaved();
      }
    } catch (e) {
      toast.error('Could not save the instruction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack gap-3">
      <div className="form-group mb-0">
        <div className="row-wrap gap-2 mb-2">
          <label className="form-label mb-0" htmlFor={textareaId}>Instruction</label>
          <InfoTip label={`About the ${s.name} instruction`}>
            What describe_mcp_server tells the agent about {s.name} before listing its tools:
            what the server is for, when to prefer it, and any limit worth knowing before a
            call. The agent reads it at its next describe_mcp_server call. Each tool&apos;s
            arguments are in that tool&apos;s own document, so leave them out.
          </InfoTip>
          {dirty && <Badge tone="warn" dot className="ml-auto">Unsaved changes</Badge>}
        </div>
        <textarea
          id={textareaId}
          className="form-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          readOnly={!isSuper}
          aria-describedby={hintId}
          aria-invalid={tooLong || undefined}
          rows={8}
        />
        <div id={hintId} className="row-wrap between gap-2 mt-1">
          <span className="text-sm text-faint">
            {isSuper
              ? 'Leave it empty to send the tool list alone.'
              : 'Only a superadmin can change it.'}
          </span>
          <span className={cx('text-sm num', tooLong ? 'text-danger' : 'text-faint')}>
            {length.toLocaleString()} / {maxChars.toLocaleString()} characters
          </span>
        </div>
      </div>

      {isSuper && (
        <div className="row-wrap gap-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={save}
                  disabled={saving || !dirty || tooLong}>
            {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save instruction'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setDraft(baseline)} disabled={saving || !dirty}>
            Discard
          </button>
        </div>
      )}

      {unavailable.length > 0 && (
        <div className="notice notice-warn mb-0">
          <Icon name="alert" />
          <div className="notice-body">
            The saved instruction names {unavailable.length === 1 ? 'a tool' : 'tools'} the
            agent cannot call here: <span className="mono">{unavailable.join(', ')}</span>.
            Take {unavailable.length === 1 ? 'it' : 'them'} out, or the agent will try.
          </div>
        </div>
      )}

      <div>
        <Disclosure label="What the agent gets now">
          <div className="row-wrap between gap-2 mb-2">
            <span className="text-sm text-muted">
              The stored result of describe_mcp_server for <code>{s.tool_id}</code>: the saved
              instruction, then the tools.
              {dirty && ' Save to update it.'}
            </span>
            <CopyButton value={s.overview} label={`Copy what ${s.name} returns`} />
          </div>
          <pre className="code-block pre-wrap break m-0" style={{ maxHeight: 360, overflowY: 'auto' }}>
            {s.overview}
          </pre>
        </Disclosure>

        {s.server_notes && (
          <Disclosure label={`${s.name}'s own notes, from the last sync`}>
            <div className="row-wrap between gap-2 mb-2">
              <span className="text-sm text-muted">
                What the server says about itself when it connects. Not sent to the agent:
                copy what is useful into the instruction.
              </span>
              <CopyButton value={s.server_notes} label={`Copy ${s.name}'s notes`} />
            </div>
            {notesUnavailable.length > 0 && (
              <p className="text-sm text-warn mb-2">
                They name tools the agent cannot call here:
                {' '}<span className="mono">{notesUnavailable.join(', ')}</span>. Leave those
                out of the instruction.
              </p>
            )}
            <pre className="code-block pre-wrap break m-0" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {s.server_notes}
            </pre>
          </Disclosure>
        )}

        {s.reference_url && (
          <p className="text-sm text-faint mt-3 mb-0">
            {s.name}&apos;s documentation:
            {' '}<a href={s.reference_url} target="_blank" rel="noopener noreferrer">
              {s.reference_url}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

function WebSearchConfig({ defaults, handleSave, saving }) {
  return (
    <div className="card">
      <SectionHead icon="search" title="Web search" />
      <p className="section-lede">
        The Brave Search key behind the agent's <code>web_search</code> tool. A new key is
        used from the next search.
      </p>
      <div className="form-row">
        <ConfigField
          label="API key"
          configKey="brave_api_key"
          secret
          defaults={defaults}
          onSave={handleSave}
          saving={saving.brave_api_key}
          placeholder="Enter Brave API key…"
          className="mb-0"
        />
      </div>
    </div>
  );
}

/* Internet access: the sites agents may not reach (`web_blocklist`, 2026-10-03).

   proxy-web, the platform's egress proxy, refuses them for every connection an agent
   opens itself, and proxy-tools refuses them for the pages it fetches for an agent, both
   with "This website is not accessible by the agent". The list is parsed by the same code
   on save (backend services/config.py), so an entry the proxy could not use is refused
   here instead of being skipped there in silence.

   Holds a draft for the same reason as CorrectionPromptEditor: a list typed into a
   textarea cannot save per keystroke. Re-seeded only when the SAVED value changes. */
function WebBlocklistConfig({ defaults, handleSave, saving }) {
  const textareaId = useId();
  const hintId = useId();
  const saved = defaults?.web_blocklist_display || '';
  const [draft, setDraft] = useState(saved);
  const [baseline, setBaseline] = useState(saved);

  useEffect(() => {
    if (saved !== baseline) {
      setBaseline(saved);
      setDraft(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const entries = baseline.split('\n').map(line => line.split('#')[0])
    .flatMap(line => line.split(/[,\s]+/)).filter(Boolean).length;
  const dirty = draft !== baseline;

  return (
    <div className="card">
      <SectionHead
        icon="shield"
        title="Internet access"
        status={entries
          ? <Badge tone="info" dot>{entries} blocked</Badge>
          : <Badge tone="neutral">Nothing blocked</Badge>}
        tip={(
          <InfoTip label="About internet access" width={360}>
            A blocked site answers "This website is not accessible by the agent". Always for
            the pages the platform fetches for an agent, and for the agent's own connections
            where this environment routes them through the platform's proxy, which also
            records every site reached (Dashboard, Outbound Internet). Live within a minute.
          </InfoTip>
        )}
      />
      <p className="section-lede">The sites no agent may reach.</p>

      <div className="form-group mb-0">
        <div className="row-wrap gap-2 mb-2">
          <label className="form-label mb-0" htmlFor={textareaId}>Blocked sites</label>
          {dirty && <Badge tone="warn" dot className="ml-auto">Unsaved changes</Badge>}
        </div>
        <textarea
          id={textareaId}
          className="form-input mono"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          rows={6}
          placeholder={'example.com\n*.tracker.example\n203.0.113.0/24'}
          aria-describedby={hintId}
        />
        <div className="row-wrap gap-2 mt-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => handleSave('web_blocklist', draft.trim())}
            disabled={saving || !dirty}
          >
            {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save list'}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setDraft(baseline)}
            disabled={saving || !dirty}
          >
            Discard
          </button>
        </div>
        <p className="form-hint" id={hintId}>
          One per line, or separated by commas. A name also blocks the names under it, and an
          IP address or a range like 203.0.113.0/24 blocks it whatever name leads there. A
          pasted URL counts as its site; # starts a comment.
        </p>
      </div>
    </div>
  );
}

function Tools() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === params.get('tab')) ? params.get('tab') : 'external';
  // In the URL so a link or a reload lands on the same tab; `replace` so changing tabs does
  // not fill the browser history. The first tab is the default and leaves no parameter.
  const setTab = useCallback((value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value === 'external') next.delete('tab'); else next.set('tab', value);
    return next;
  }, { replace: true }), [setParams]);
  const phoneOn = usePhoneCalls();
  const [defaults, setDefaults] = useState(null);
  const [models, setModels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [voiceModels, setVoiceModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [defResp, modResp, provResp, voiceResp] = await Promise.all([
          apiFetch('/api/config/defaults'),
          apiFetch('/api/llm-models'),
          apiFetch('/api/llm-providers'),
          apiFetch('/api/voice-models'),
        ]);
        if (defResp.ok) setDefaults(await defResp.json());
        if (modResp.ok) setModels(await modResp.json());
        if (provResp.ok) setProviders(await provResp.json());
        if (voiceResp.ok) setVoiceModels((await voiceResp.json()).models || []);
        if (!defResp.ok || !modResp.ok || !provResp.ok || !voiceResp.ok) setLoadError(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoadError(true);
      } finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const fetchDefaults = async () => {
    try {
      const response = await apiFetch('/api/config/defaults');
      if (response.ok) setDefaults(await response.json());
    } catch (error) { console.error('Error fetching defaults:', error); }
  };

  const handleSave = async (key, value) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const response = await apiFetch('/api/config/defaults', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value }),
      });
      if (response.ok) {
        toast.success('Updated');
        fetchDefaults();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch (error) { toast.error('Failed to update'); }
    finally { setSaving(prev => ({ ...prev, [key]: false })); }
  };

  const header = (
    <PageHeader description="What the agent can reach, and the models behind each capability.">
      <h1 className="page-title">Tools</h1>
    </PageHeader>
  );

  // Every panel stays mounted, as RossGate's two tabs do, so a half-typed prompt, blocklist
  // or language table survives a change of tab. While loading, each shows skeletons.
  const panel = (value, content) => (
    <div role="tabpanel" id={`tools-panel-${value}`} aria-labelledby={`tools-tab-${value}`}
         hidden={tab !== value}>
      {loading ? <><CardSkeleton lines={6} /><CardSkeleton lines={4} /></> : content}
    </div>
  );

  return (
    <div>
      {header}
      <div className="page-tabs">
        <Tabs variant="line" label="Tools sections" idPrefix="tools" value={tab}
              onChange={setTab} tabs={TABS} />
      </div>

      {/* Without this, a failed load renders every value as "Missing", which reads as
          "nothing is configured" and invites re-entering keys that are already set. Above
          the panels, so it shows on every tab. */}
      {loadError && (
        <div className="notice notice-danger">
          <Icon name="alertCircle" />
          <div className="notice-body">
            <div className="notice-title">Some settings did not load</div>
            What is shown below may not match what is saved. Reload the page before changing
            anything.
          </div>
        </div>
      )}

      {/* External. Internet access first: it decides what every connection an agent makes
          can reach, not one tool. Outbound correction after the tools: it is the only
          section that changes what every user RECEIVES rather than what an agent can do.
          The defaults for new users close the tab: they are about users, not settings. */}
      {panel('external', (
        <>
          <WebBlocklistConfig defaults={defaults} handleSave={handleSave}
                              saving={!!saving.web_blocklist} />
          <WebSearchConfig defaults={defaults} handleSave={handleSave} saving={saving} />
          <CaptioningConfig
            defaults={defaults}
            handleSave={handleSave}
            saving={saving}
            models={models}
            providers={providers}
          />
          <OutboundCorrectionConfig
            defaults={defaults}
            handleSave={handleSave}
            saving={saving}
            models={models}
            providers={providers}
          />
          <PlatformToolDefaults />
        </>
      ))}

      {/* MCP. RossGate first: it gates a whole family of tools rather than one, so a
          misconfiguration there is the likeliest reason an agent has lost a capability. The
          MCP servers card renders nothing where the environment has not opted in. */}
      {panel('mcp', (
        <>
          <RossGateConfig defaults={defaults} handleSave={handleSave} saving={saving} />
          <McpServersConfig defaults={defaults} handleSave={handleSave} saving={saving} />
        </>
      ))}

      {/* Voice, in the order a voice message travels: heard, answered, then the calls. */}
      {panel('voice', (
        <>
          <SpeechToTextConfig defaults={defaults} handleSave={handleSave} saving={saving}
                              voiceModels={voiceModels} />
          <TextToSpeechConfig />
          {phoneOn === true && (
            <PhoneCallsCard defaults={defaults} handleSave={handleSave} saving={saving} />
          )}
        </>
      ))}
    </div>
  );
}

export default Tools;
